import { Component, OnInit, OnDestroy, HostListener, NgZone, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as _ from 'lodash';
import * as moment from 'moment';
import { PersonsService } from 'src/app/core/services/persons.service';
import PersonsModel, {
  IAddress,
  IFarmLocations,
  IPersonType,
  addUserToAclObj,
  sendPasswordToMobileEmailObj
} from '../../../core/models/persons.model';
import { BusinessEntityService } from '../../../core/services/business.service';
import BusinessEntity from '../../../core/models/business.model';
import { FarmSiteService } from '../../../core/services/farm-site.service';
import FarmSiteModel from '../../../core/models/farm-site.model';
import { PondSectionService } from '../../../core/services/pond-section.service';
import PondSectionModel from '../../../core/models/pond-section.model';
import { CustomerPondService } from '../../../core/services/customer-pond.service';
import CustomerPondModel from '../../../core/models/customer-pond.model';
import { PersonRoleService } from '../../../core/services/person-role.service';
import PersonRoleModel from 'src/app/core/models/person-role.model';
import { BusinessTypeService } from 'src/app/core/services/business-type.service';
import BusinessTypeModel from 'src/app/core/models/business-type.model';
import ProductOrdersModel from 'src/app/core/models/orders.model';
import { ProductOrdersService } from 'src/app/core/services/order.service';
import LocationTrackerModel from 'src/app/core/models/location-tracker.model';
import { LocationTrackerService } from 'src/app/core/services/location-tracker.service';
import FeedLogModel from 'src/app/core/models/feedlog.model';
import SamplingModel from 'src/app/core/models/sampling.model';
import HarvestLogModel from 'src/app/core/models/harvestlog.model';
import { FeedLogService } from 'src/app/core/services/feedlog.service';
import { TreatmentLogService } from 'src/app/core/services/treatmentlog.service';
import { ObservationLogService } from 'src/app/core/services/observationlog.service';
import { SamplingService } from 'src/app/core/services/sampling.service';
import { HarvestLogService } from 'src/app/core/services/harvestlog.service';
import SessionModel from 'src/app/core/models/session.model';
import { SessionService } from 'src/app/core/services/session.service';
import { ToastrService } from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { CountriesAPIService } from 'src/app/core/services/countries.service';
import CountriesModel from 'src/app/core/models/countries.model';
import { StateModel, VillageModel } from 'src/app/core/models/json.model';
import { StatesAPIService } from 'src/app/core/services/states.service';
import { ProviderVisitsStatsService } from 'src/app/core/services/provider-visits-stats.service';
import { ProductPlacementsService } from 'src/app/core/services/product-placements.service';
import { BatchLoader } from "../../../core/utilities/batch-loader.util";
import { MatOptionSelectionChange } from '@angular/material/core';

declare var $: any;

/**
 * @component PersonsListComponent
 * @selector app-persons-list
 * @description
 * The `PersonsListComponent` manages the "People" or "Staff" section within the Trading Community Admin Panel.
 * It serves as a central hub for managing user accounts and staff members associated with various business entities
 * (Farms, Providers, etc.).
 *
 * ## Key Responsibilities:
 * 1. **User Management**: Lists, creates, and updates person records (users/staff).
 * 2. **Role-Based Access Control**: Adapts the view and available actions based on the logged-in user's role
 *    (e.g., Business Admin, Farmer, Farm Manager, Provider Manager, Employee).
 * 3. **Hierarchical Data Handling**: Manages relationships between Persons, Business Entities, Farm Sites, Sections, and Ponds.
 * 4. **Address Management**: Provides cascading dropdowns for Country, State, and Village selection.
 * 5. **Profile View**: Displays detailed activity logs (Feed, Treatment, Harvest, etc.) and location tracking for specific persons (e.g., Technicians).
 * 6. **Access Control List (ACL) Integration**: Handles user account creation in the ACL system and password generation/distribution.
 *
 * ## Features:
 * - **Infinite Scroll**: Uses `BatchLoader` for efficient pagination of large user lists.
 * - **Dynamic Forms**: Reactive forms for adding/updating persons with validation.
 * - **Multi-Select Dropdowns**: For assigning multiple sections or ponds to a person.
 * - **Search & Filter**: Client-side filtering of the person list.
 * - **Geo-Location**: Integration with location services for address selection.
 * - **Activity Logs**: Aggregates various logs (Feed, Harvest, etc.) for technician profiles.
 *
 * ## User Roles & Permissions:
 * - **Employee**: Can view and manage persons across all businesses.
 * - **Business Admin (Farm)**: Can manage staff (Farmers, Farm Managers, Techs) within their farm business.
 * - **Farmer**: Can manage Farm Managers and Techs.
 * - **Farm Manager**: Can manage Farm Techs and Investors.
 * - **Business Admin (Provider)**: Can manage Provider Managers and Technicians.
 * - **Provider Manager**: Can manage Technicians assigned to them.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: Determines the role of the logged-in user.
 * - `businessID`: The ID of the logged-in user's business.
 * - `personID`: The ID of the logged-in user.
 * - `loginUserBusinessCategory`: 'Farm' or 'Provider'.
 *
 * ## Data Flow:
 * 1. **Initialization (`ngOnInit`)**:
 *    - Loads user context from `localStorage`.
 *    - Fetches reference data (Roles, Business Types, Countries).
 *    - Loads the initial list of persons based on the user's scope.
 * 2. **Creation (`personDetailsSubmit`)**:
 *    - Validates form input.
 *    - Creates a `PersonsModel` record.
 *    - Creates a corresponding user in the ACL system.
 *    - Generates a password and sends it via SMS/Email.
 * 3. **Update (`personUpdateDetailsSubmit`)**:
 *    - Updates person details.
 *    - Updates related records (e.g., reassignment of orders if a technician is updated).
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-persons-list',
  templateUrl: './persons-list.component.html',
  styleUrls: ['./persons-list.component.scss']
})
export class PersonsListComponent implements OnInit, OnDestroy {
  // ==========================================================================
  // DATA MODELS & LISTS
  // ==========================================================================

  /** @type {PersonsModel[]} List of persons currently displayed (paginated/filtered). */
  public persons: PersonsModel[] = [];
  /** @type {PersonsModel[]} Full list of persons available for the current context. */
  public disptotalPersonsList: PersonsModel[] = [];
  /** @type {PersonsModel} Current person model being added or edited. */
  public person: PersonsModel = new PersonsModel();
  /** @type {IAddress} Address model for the person. */
  public address: IAddress;
  /** @type {IPersonType} Person type model. */
  public personType: IPersonType;
  /** @type {IFarmLocations} Farm location association model. */
  public farmLocation: IFarmLocations;
  /** @type {Array<BusinessEntity>} List of available business entities. */
  public businessEntities: Array<BusinessEntity>;
  /** @type {BusinessEntity} Selected business entity. */
  public businessEntity: BusinessEntity;
  /** @type {Array<FarmSiteModel>} List of farm sites for selection. */
  public farmSites: Array<FarmSiteModel>;
  /** @type {Array<PondSectionModel>} List of pond sections. */
  public pondSections: Array<PondSectionModel>;
  /** @type {PondSectionModel} Selected pond section. */
  public pondSection: PondSectionModel;
  /** @type {Array<CustomerPondModel>} List of customer ponds. */
  public customerPonds: Array<CustomerPondModel>;
  /** @type {CustomerPondModel} Selected customer pond. */
  public customerPond: CustomerPondModel;
  /** @type {Array<PersonRoleModel>} List of available person roles. */
  public roles: Array<PersonRoleModel>;
  /** @type {Array<GeoLocationModel>} List of geo-locations (villages/pincodes). */
  /** @type {Array<BusinessTypeModel>} List of business types. */
  public businessTypes: Array<BusinessTypeModel>;
  /** @type {BusinessTypeModel} Selected business type. */
  public btype: BusinessTypeModel;
  /** @type {addUserToAclObj} Object for creating ACL user. */
  public addUserToAcl: addUserToAclObj;
  /** @type {sendPasswordToMobileEmailObj} Object for sending password credentials. */
  public sendPwdTouser: sendPasswordToMobileEmailObj;

  // ==========================================================================
  // UI STATE & FLAGS
  // ==========================================================================

  /** @type {boolean} Flag for page loading state. */
  public pgLoad: boolean = false;
  /** @type {boolean} Flag for response loading state (spinner). */
  public responseLoader: boolean = false;
  /** @type {string} Login person type from localStorage. */
  public loginPersonType: string;
  /** @type {string} Search keyword for filtering list. */
  public search: string;
  /** @type {number} Current page number for pagination. Default 1. */
  public currentPageNumber = 1;
  /** @type {number} Items per page. Default 15. */
  public noIfItemsPerPage = 15;
  /** @type {boolean} Flag to hide business details block in form. */
  public hideBusinessDetailsBlock: boolean;
  /** @type {string} Selected business type ID. */
  public businessType: string;
  /** @type {any} Data for pond details dropdown. */
  public getPondsDetailsData: any;
  /** @type {string} Selected person type name. */
  public persontype: string;
  /** @type {FormGroup} Reactive form for adding/editing person. */
  public addPerson: FormGroup;
  /** @type {boolean} Flag indicating if form is submitted. */
  public submitted: boolean;
  /** @type {boolean} Flag indicating if sections already exist. */
  public isexistingSections: boolean;

  // ==========================================================================
  // DROPDOWN SETTINGS & LISTS
  // ==========================================================================

  /** @type {Array} Dropdown list for sections. */
  public sections_dropdownList = new Array();
  /** @type {Array} Selected items for sections. */
  public sections_selectedItems = new Array();
  /** @type {Object} Settings for sections dropdown. */
  public sections_dropdownSettings = {};
  /** @type {string} Login person ID. */
  public loginpersonID: string;
  /** @type {string} Login business ID. */
  public loginbusinessID: string;
  /** @type {Array} Dropdown list for ponds. */
  public ponds_dropdownList = new Array();
  /** @type {Array} Selected items for ponds. */
  public ponds_selectedItems = new Array();
  /** @type {Object} Settings for ponds dropdown. */
  public ponds_dropdownSettings = {};
  /** @type {Array} List of business admin persons. */
  public businessAdminPersons = new Array();
  /** @type {Array<PersonsModel>} List of BA business persons. */
  public baBusinessPersons: Array<PersonsModel>;
  /** @type {Array} Dropdown list for sites. */
  public sitedropdownList = new Array();
  /** @type {Array} Selected items for sites. */
  public siteselectedItems = new Array();
  /** @type {Object} Settings for sites dropdown. */
  public sitedropdownSettings = {};

  // ==========================================================================
  // UPDATE & PROFILE PROPERTIES
  // ==========================================================================

  /** @type {string} Person type ID for update. */
  public personTypeID: string;
  /** @type {string} Input business ID for update. */
  public inputbusinessID: string;
  /** @type {boolean} Flag to show submit button. */
  public submitButton: boolean;
  /** @type {boolean} Flag to show update button. */
  public updateButton: boolean;
  /** @type {Array} Total section dropdowns available. */
  public totalsectiondropdowns = new Array();
  /** @type {Array} Total ponds dropdowns available. */
  public totalpondsdropdowns = new Array();
  /** @type {any} Data for section details. */
  public getSectionsDetailsData: any;
  /** @type {boolean} Flag to show address form. */
  public showAddressform: boolean;
  /** @type {string} Login user business name. */
  public loginUserBusinessName: string;
  /** @type {string} Login user business category. */
  public loginUserBusinessCategory: string;
  /** @type {string} Login person name. */
  public loginPersonName: string;
  /** @type {boolean} Flag to display farmer specific fields. */
  public farmerDisplay: boolean;
  /** @type {any} Selected country object. */
  public country: any;
  /** @type {string} Keyword for search. */
  public keyword: string;
  /** @type {Array} List of person roles for dropdown. */
  public personRoles = new Array();
  /** @type {Array<PersonsModel>} List of persons filtered by business. */
  public personsByBusiness: Array<PersonsModel>;
  /** @type {Array<VillageModel>} List of villages. */
  public villagesList: Array<VillageModel>;
  /** @type {Array<PersonsModel>} Display list of BA business persons. */
  public disptotalBaBusinessPersons: Array<PersonsModel>;
  /** @type {Array<ProductOrdersModel>} List of product orders for profile. */
  public productOrders: Array<ProductOrdersModel>;
  /** @type {Array<LocationTrackerModel>} List of location trackers for profile. */
  public getAllLocationTrackers: Array<LocationTrackerModel>;
  /** @type {Array<FeedLogModel>} List of feed logs for profile. */
  public getAllFeedlogActivities: Array<FeedLogModel>;
  /** @type {Array<any>} List of treatment logs for profile. */
  public getAllTreatmentLogActivities: Array<any>;
  /** @type {Array<any>} List of observation logs for profile. */
  public getAllObservationLogActivities: Array<any>;
  /** @type {Array<SamplingModel>} List of sampling activities for profile. */
  public getAllSamplingAcytivities: Array<SamplingModel>;
  /** @type {Array<HarvestLogModel>} List of harvest logs for profile. */
  public getAllHarvestLogActivities: Array<HarvestLogModel>;
  /** @type {Array<FarmSiteModel>} List of all farm sites. */
  public getAllFarmsites: Array<FarmSiteModel>;
  /** @type {Array} Levels array for hierarchy. */
  public level = new Array();
  /** @type {boolean} Flag for level close state. */
  public levelClose: boolean;
  /** @type {boolean} Flag for level open state. */
  public levelOpen: boolean;
  /** @type {boolean} Flag for customer close state. */
  public customerClose: boolean;
  /** @type {boolean} Flag for customer open state. */
  public customerOpen: boolean;
  /** @type {string} Parameter for field force type (People/Staff). */
  public paramFieldForce: string;
  /** @type {any} Person profile data object. */
  public personProfile: any;
  /** @type {Array<BusinessEntity>} Business entities for profile. */
  public businessEntitiesProfile: Array<BusinessEntity>;
  /** @type {any} Application permissions. */
  public appPermission: any;
  /** @type {string} Business keyword for search. */
  public businesskeyword: string;
  /** @type {any} Selected business item. */
  public businessItem: any;
  /** @type {any} Business type rule. */
  public businessTypeRule: any;
  /** @type {boolean} Flag for no data message. */
  public NoDataMessage: boolean = false;
  /** @type {any} Generated password object. */
  public passowrdGenerated: any;
  /** @type {any} ACL user creation response. */
  public addUsertoacl: any;
  /** @type {string} Password sent status. */
  public passowrdSent: string;
  /** @type {String} Dialing code for mobile number. */
  public dialingCode: String;
  /** @type {boolean} Flag to prevent double click on creation. */
  public personCreationDoubleClick: boolean;
  /** @type {number} Active page number. */
  public activePageNo: number;
  /** @type {boolean} Flag to clear search input. */
  public clearSearchInput: boolean;
  /** @type {string} Date string for last week. */
  public weekfromDate: string;
  /** @type {string} Date string for last year. */
  public yearfromDate: string;
  /** @type {string} Date string for last month. */
  public monthfromDate: string;
  /** @type {string} Today's date string. */
  public todayDate: string;
  /** @type {number} Count of business persons. */
  public businessPersonsCount: number;
  /** @type {boolean} Flag to disable business selection. */
  public businessDisabled: boolean;
  /** @type {boolean} Flag to disable person role selection. */
  public personRoleDisabled: boolean;
  /** @type {boolean} Flag to disable person role assignment. */
  public personRoleAssignDisabled: boolean;
  /** @type {string} Login person mobile number. */
  public loginPersonMobileNumber: string;
  /** @type {any} Available BA persons. */
  public BAPersonsAvailable: any;
  /** @type {SessionModel} Session model. */
  public sessionModel: SessionModel;
  /** @type {any} Person number. */
  public personNumber: any;
  /** @type {boolean} Flag indicating if user is a chat user. */
  public chatUser: boolean;
  /** @type {any} Generic ID. */
  public id: any;
  /** @type {any} Subscription for route parameters. */
  private paramSubscription: any;
  /** @type {number} Notification alert count. */
  public notificationAlertCount: number = 1;
  /** @type {boolean} User loader flag. */
  public userLoader: boolean;
  /** @type {any} Duplicate business admin persons list. */
  public dupBusinessAdminPersons: any;
  /** @type {Array} List of countries. */
  public countries = new Array();
  /** @type {Array<StateModel>} List of states. */
  public states: Array<StateModel>;
  /** @type {Array<VillageModel>} List of villages. */
  public villages: Array<VillageModel>;
  /** @type {boolean} Flag for country dropdown state. */
  public isCountryDropdown = false;
  /** @type {boolean} Flag for state dropdown state. */
  public isStateDropdown = false;
  /** @type {boolean} Flag for village dropdown state. */
  public isVillageDropdown = false;
  /** @type {any[]} Filtered list of countries. */
  public filteredCountriesList: any[] = [];
  /** @type {any[]} Filtered list of states. */
  public filteredStatesList: any[] = [];
  /** @type {any[]} Filtered list of villages. */
  public filteredVillagesList: any[] = [];
  /** @type {any[]} Limited list of villages for display. */
  public limitedVillagesList: any[] = [];
  /** @type {string} Search text for country. */
  public countrySearch: string = 'Enter Country name';
  /** @type {string} Search text for state. */
  public stateSearch: string = 'Enter State name';
  /** @type {string} Search text for village. */
  public villageSearch: string = 'Enter Village name';
  /** @type {boolean} Flag indicating country changed. */
  public isCountryChanged: boolean = false;
  /** @type {boolean} Flag indicating state changed. */
  public isStateChanged: boolean = false;
  /** @type {boolean} Flag indicating village changed. */
  public isVillageChanged: boolean = false;
  /** @type {boolean} Flag for list loader spinner. */
  public listLoader = false;              // Show listLoader spinner
  /** @type {number} Start index for batch loading. */
  public startIndex = 0;             // Tracks how much has been loaded
  /** @type {boolean} Flag indicating filtering is active. */
  public isFiltering: boolean = false;
  /** @type {boolean} Flag indicating empty list. */
  public noList: boolean = false;
  /** @type {boolean} Flag to enable/disable page loader. */
  public enableDisablePageLoader: boolean = false;
  /** @type {any} Search timeout reference. */
  searchTimeout: any;
  /** @type {BatchLoader<any>} Batch loader utility instance. */
  private batchLoader: BatchLoader<any>;
  /** @type {Array} Total list of persons (unfiltered). */
  public totalPersonsList = [];

  /** @type {number} Count of form fields. */
  public formFieldCount: number = 0;
  /** @type {boolean} Flag to show/hide the form. */
  public showForm: boolean = false;
  /** @type {any[]} Filtered list of businesses. */
  public filteredBusinessesList: any[] = [];

  /**
   * @constructor
   * @description Initializes the component, services, and reactive form.
   * @param {ToastrService} toasterService - Service for displaying notifications.
   * @param {SessionService} sessionService - Service for session management.
   * @param {LocationTrackerService} locationTrackerService - Service for location tracking.
   * @param {ProductOrdersService} productOrdersService - Service for product orders.
   * @param {BusinessTypeService} businessTypeService - Service for business types.
   * @param {GeoLocationService} geoLocationService - Service for geo-location data.
   * @param {PersonRoleService} personRoleService - Service for person roles.
   * @param {FarmSiteService} farmSiteService - Service for farm sites.
   * @param {PondSectionService} pondSectionService - Service for pond sections.
   * @param {CustomerPondService} customerPondService - Service for customer ponds.
   * @param {PersonsService} personService - Service for person management.
   * @param {BusinessEntityService} businessEntityService - Service for business entities.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {ActivatedRoute} route - Service for accessing route parameters.
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @param {Router} router - Service for navigation.
   * @param {ProductPlacementsService} productPlacementsService - Service for product placements.
   * @param {FeedLogService} feedlogservice - Service for feed logs.
   * @param {TreatmentLogService} treatmlogservice - Service for treatment logs.
   * @param {ObservationLogService} observationlogservice - Service for observation logs.
   * @param {SamplingService} samplingservice - Service for sampling data.
   * @param {HarvestLogService} harvestlogservice - Service for harvest logs.
   * @param {WaterReadingsService} waterreadingservice - Service for water readings.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking previous routes.
   * @param {CountriesAPIService} countriesAPIService - Service for country data.
   * @param {StatesAPIService} statesAPIService - Service for state data.
   * @param {ProviderVisitsStatsService} providerVisitsStatsService - Service for provider visit stats.
   * @param {NgZone} ngZone - Service for executing work inside or outside of the Angular zone.
   * @param {ChangeDetectorRef} cdr - Service for change detection.
   */
  constructor(public toasterService: ToastrService, public sessionService: SessionService, private locationTrackerService: LocationTrackerService, private productOrdersService: ProductOrdersService, public businessTypeService: BusinessTypeService, public personRoleService: PersonRoleService, public farmSiteService: FarmSiteService, public pondSectionService: PondSectionService, public customerPondService: CustomerPondService, public personService: PersonsService, public businessEntityService: BusinessEntityService, public formBuilder: FormBuilder, public route: ActivatedRoute, public router: Router,
    public productPlacementsService: ProductPlacementsService,
    public feedlogservice: FeedLogService, public treatmlogservice: TreatmentLogService, public observationlogservice: ObservationLogService, public samplingservice: SamplingService, public harvestlogservice: HarvestLogService, public commonMethodsService: CommonMethodsService, private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService, public countriesAPIService: CountriesAPIService, public statesAPIService: StatesAPIService, private providerVisitsStatsService: ProviderVisitsStatsService, private ngZone: NgZone, private cdr: ChangeDetectorRef

  ) {
    this.person = new PersonsModel();
    this.address = new IAddress();
    this.personType = new IPersonType();
    this.farmLocation = new IFarmLocations();
    this.addUserToAcl = new addUserToAclObj();
    this.sendPwdTouser = new sendPasswordToMobileEmailObj();
    /*validation*/
    this.addPerson = this.formBuilder.group({
      person: ['', Validators.required],
      businessType: [null],
      formsiteId: [null],
      pondId: [null],
      sections: [null],
      personFirstName: ['', [Validators.required,
      Validators.minLength(3),
      Validators.maxLength(30),
      Validators.pattern('^[a-zA-Z ]*$')
      ]],
      personLastName: ['', [Validators.required,
      Validators.minLength(1),
      Validators.maxLength(30),
      Validators.pattern('^[a-zA-Z ]*$')
      ]],
      personMobileNumber: ['', Validators.required],
      email: ['', [Validators.pattern('[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,6}$')]],
      employeeDesignation: ['', [Validators.maxLength(30), Validators.pattern('[a-zA-Z ]*$')]],
      employeeCode: ['', [Validators.minLength(3), Validators.maxLength(10), Validators.pattern('[a-zA-Z0-9]*$')]],
      countryName: ['', [Validators.required]],
      stateName: ['', [Validators.required]],
      villageName: ['', [Validators.required]],
      level: ['', Validators.required],
      pincode: ['', [Validators.required,
      Validators.minLength(6),
      Validators.maxLength(6),
      Validators.pattern('^[0-9]{6,6}$')
      ]],
      dialCode: [null],
      enablesection: [null]
    });
    /*validation end*/

    this.formFieldCount = Object.keys(this.addPerson.controls).length;
  }

  /**
   * @method ngOnInit
   * @lifecycle angular
   * @async
   * @description Initializes the component, sets up route subscriptions, and triggers data loading.
   * @returns {Promise<void>}
   */
  public async ngOnInit() {
    window.scrollTo(0, 0);
    this.isFiltering = false;
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.paramSubscription = this.route.params.subscribe(params => {
      this.paramFieldForce = params.isAdmin;
      this.onInit();
    });
  }

  /**
   * @method getDialCode
   * @description Retrieves the dialing code for the selected country and updates the person model.
   * @returns {void}
   */
  public getDialCode() {
    let item = this.countries.find(code => code.name == this.countrySearch);
    this.person.dialingCode = String(item.dialingCode[0]);
  }

  /**
   * @method onInit
   * @async
   * @description Main initialization logic.
   * - Loads user context from localStorage.
   * - Fetches initial data (countries, roles, business types).
   * - Loads person list based on user role and business category.
   */
  public async onInit() {
    this.appPermission = this.route.snapshot.data['rolepermission'];
    this.search = '';
    this.keyword = 'name';
    this.businesskeyword = 'businessName';
    this.pgLoad = false;
    this.personNumber = undefined;
    this.businessEntities = [];
    this.disptotalPersonsList = [];
    this.baBusinessPersons = [];
    this.disptotalBaBusinessPersons = [];
    this.businessAdminPersons = [];
    this.personCreationDoubleClick = false;
    this.getAllFeedlogActivities = [];
    this.getAllTreatmentLogActivities = [];
    this.getAllObservationLogActivities = [];
    this.getAllSamplingAcytivities = [];
    this.getAllHarvestLogActivities = [];
    this.roles = [];
    this.businessTypes = [];
    try {
      this.clearSearchInput = false;
      this.businessPersonsCount = 0;
      this.businessDisabled = false;
      this.personRoleDisabled = false;
      this.personRoleAssignDisabled = false;
      this.levelClose = true;
      this.levelOpen = false;
      this.customerClose = true;
      this.customerOpen = false;
      this.responseLoader = true;
      // get login person type
      this.businessEntities = new Array();
      this.farmSites = new Array();
      this.getPondsDetailsData = new Array();
      this.isexistingSections = false;
      this.baBusinessPersons = new Array();
      this.businessAdminPersons = new Array();
      this.personRoles = new Array();
      this.submitButton = false;
      this.updateButton = false;
      this.farmerDisplay = false;
      this.businessType = '';
      this.person.farmSiteId = undefined;
      this.persontype = undefined;
      this.person.workLocation = undefined;
      this.person.pondId = undefined;
      this.countries = await this.countriesAPIService.getAllCountriesList();
      if (this.countries) {
        this.countrySearch = 'India';
        this.address.country = 'India';
        const indianCountryIndexPosition = this.countries.findIndex((country) => country.name == 'India');
        if (indianCountryIndexPosition != -1) {
          const objectToMove = this.countries.splice(indianCountryIndexPosition, 1);
          this.countries.unshift(objectToMove[0]);
          this.filteredCountriesList = this.countries;
          this.getDialCode();
          this.countrySelected(objectToMove[0]);
        }
      }
      this.loginPersonType = localStorage.getItem('loginPersonType');
      if (localStorage.length > 0) {
        this.loginpersonID = localStorage.personID;
        this.loginbusinessID = localStorage.businessID;
        this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
        this.loginPersonName = localStorage.getItem('LoginUserName');
        this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
        this.loginPersonMobileNumber = localStorage.getItem('loginPersonMobileNumber');
      }
      const myInputControl = this.addPerson.get('businessType');
      const validators = myInputControl.validator ? [myInputControl.validator, Validators.required] : Validators.required;
      myInputControl.setValidators(validators);
      myInputControl.updateValueAndValidity();
      this.showAddressform = false;
      // Handling side nav access
      this.BAPersonsAvailable = [];
      // get person list based on login businessid
      if ((this.loginPersonType == 'Business Admin' || this.loginPersonType == 'Provider Manager') && this.loginUserBusinessCategory == 'Provider') {
        this.roles = await this.personRoleService.getAllPersonRoles();
        this.initDates();
        let businessAdminPersons: any;
        this.dupBusinessAdminPersons = [];
        if (this.paramFieldForce == 'People') {
          this.businessItem = undefined;
          if (this.loginPersonType == 'Business Admin') {
            businessAdminPersons = await this.personService.getPersonBusinessPersonsByBusinessID(this.loginbusinessID);
            this.disptotalBaBusinessPersons = businessAdminPersons.providerBusinessPersons;
            await this.getPersonsByBusinessId();
            this.businessEntities = await this.businessEntityService.getAllfarmerByProviderID(this.loginbusinessID);
          } else {
            let persons = await this.personService.getAllTechniciansByProviderManagerId(this.loginpersonID);
            let technicianIds = persons.length > 0 ? _.map(persons, 'id') : [];
            if (technicianIds.length > 0) {
              let technicianFarmsFarmsites = await this.farmSiteService.getFarmeSitesByTechnicianIDs(technicianIds);
              let technicianOnBoardedFarmBusinesses = technicianFarmsFarmsites.length > 0 ? _.uniqBy(_.map(technicianFarmsFarmsites, 'businessID'), 'id') : [];
              this.businessEntities = technicianOnBoardedFarmBusinesses;
              if (technicianOnBoardedFarmBusinesses.length > 0) {
                businessAdminPersons = await this.personService.getTechnicianOnBoardFarmPersonsByBusinessIds(_.map(technicianOnBoardedFarmBusinesses, 'id'));
                this.disptotalBaBusinessPersons = businessAdminPersons;
              }
            }
          }
          this.initializeBatchLoader(); // Reset instance
          this.batchLoader?.reset();     // Clear state
          await this.batchLoader?.loadNextBatch(true); // Load first batch
          this.pgLoad = true
        } else if (this.paramFieldForce == 'Staff') {
          if (this.loginPersonType == 'Business Admin') {
            businessAdminPersons = await this.personService.getPersonBusinessPersonsByBusinessID(this.loginbusinessID);
            if (businessAdminPersons.persons.length > 0) {
              let data = new Array();
              data = businessAdminPersons.persons;
              data.push({ providerBusinessPersons: businessAdminPersons.providerBusinessPersons });
              this.disptotalPersonsList = [...data];
              this.pgLoad = true;
              let levels = data.map(businessPerson => businessPerson && businessPerson.level);
              this.level = levels.filter((item, pos) => {
                return levels.indexOf(item) == pos;
              });
              for (let i = 1; i <= this.level.length; i++) {
                let baPersons = data.filter(person => person.level === i);
                this.businessAdminPersons.push({ level: baPersons });
              }
              this.dupBusinessAdminPersons = businessAdminPersons.persons;
            } else {
              this.dupBusinessAdminPersons = [];
            }
            await this.getPersonsByBusinessId();
            this.pgLoad = true;
          } else {
            let persons = await this.personService.getAllTechniciansByProviderManagerId(this.loginpersonID);
            let data = new Array();
            data = persons;
            this.disptotalPersonsList = [...data];
            this.pgLoad = true;
            let levels = data.map(businessPerson => businessPerson && businessPerson.level);
            this.level = levels.filter((item, pos) => {
              return levels.indexOf(item) == pos;
            });
            for (let i = 1; i <= this.level.length; i++) {
              let baPersons = data.filter(person => person.level != i);
              this.businessAdminPersons.push({ level: baPersons });
            }
            this.dupBusinessAdminPersons = this.businessAdminPersons;
          }
          this.businessEntities = await this.businessEntityService.getAllfarmerByProviderID(this.loginbusinessID);
          this.businessEntity = JSON.parse(localStorage.getItem('loggedInBusiness'));
          if (this.businessEntity) {
            this.businessItem = this.businessEntity;
            this.getPersonRolesOnBusinessId(this.businessEntity);
          }
          this.addPerson.get('businessType')!.setValue(this.businessEntity.businessName)
        }
        this.hideBusinessDetailsBlock = true;
        this.businessType = localStorage.businessID;
        this.businessTypes = await this.businessTypeService.getAllBusinessTypes();
      } else {
        let persons = (await this.personService.getPersonswithBusiness()).filter(b => b.businessID?.businessType?.[0]?.businessTypeID == '618a17b80f339c001e991b03') || [];
        this.disptotalPersonsList = persons;
        this.totalPersonsList = this.disptotalPersonsList;
        this.businessEntities = (await this.businessEntityService.getAllBusiness())?.filter(b => b.businessType?.[0]?.businessTypeID == '618a17b80f339c001e991b03') || [];;
        if (this.businessEntities && this.businessEntities.length > 0) {
          this.businessEntities = this.businessEntities;
        }
        this.roles = await this.personRoleService.getAllPersonRoles();
        this.businessTypes = await this.businessTypeService.getAllBusinessTypes();
        this.initializeBatchLoader(); // Reset instance
        this.batchLoader?.reset();     // Clear state
        await this.batchLoader?.loadNextBatch(true); // Load first batch
        this.pgLoad = true;
      }
      /***************update person*******************/
      if (this.persontype == 'Farm Investor') {
        this.makeMultiSelctionDropDownUpdate();
      }
      if (this.persontype == 'Farm Investor') {
        this.makePondsMultiSelctionDropDownUpdate();
      }
      /***************update person end*******************/
      // await this.personRole();
    } catch (error) {
      console.log(error);
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method onScroll
   * @hostListener window:scroll
   * @description Detects window scroll events to trigger infinite scroll loading.
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * @type {any}
   * @private
   * @description Timeout for scroll debounce.
   */
  private scrollTimeout: any;

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Checks scroll position and triggers batch loading if near bottom.
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading() && !this.isFiltering) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Initializes the BatchLoader utility for infinite scrolling.
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => {
        if (this.loginUserBusinessCategory == 'Provider' && this.paramFieldForce == 'People') {
          return this.disptotalBaBusinessPersons;
        } else if (this.loginUserBusinessCategory == 'Farm' || this.loginPersonType == 'EMPLOYEE') {
          return this.disptotalPersonsList;
        }
      },
      (updatedList) => {
        if (this.loginUserBusinessCategory == 'Provider' && this.paramFieldForce == 'People') {
          this.baBusinessPersons = updatedList;
        } else if (this.loginUserBusinessCategory == 'Farm' || this.loginPersonType == 'EMPLOYEE') {
          this.persons = updatedList;
        }
        this.noList = updatedList.length === 0;
      },
      undefined,
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method filterBusinesses
   * @description Filters the business list based on user input in the dropdown.
   */
  public filterBusinesses(): void {
    const value = (this.addPerson.get('businessType')!.value || '') as BusinessEntity | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.businessName.toLowerCase();

    this.filteredBusinessesList = this.businessEntities.filter(o =>
      o.businessName.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method businessSelected
   * @description Handles business selection from the dropdown.
   * @param {BusinessEntity} business - The selected business entity.
   */
  public businessSelected(business: BusinessEntity): void {
    this.addPerson.get('businessType')!.setValue(business.businessName);
    this.businessType = business.id;
    this.businessItem = business;
    if (this.businessType) {
      this.persontype = undefined;
      this.farmSites = [];
      this.personRoles = [];
      this.sitedropdownList = [];
      this.siteselectedItems = [];
      this.getPersonsByBusinessId();
      this.getPersonRolesOnBusinessId(business);
    }
  }

  /**
   * @method filterCountries
   * @description Filters the country list based on user input.
   */
  public filterCountries(): void {
    const value = (this.addPerson.get('countryName')!.value || '') as CountriesModel | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.name.toLowerCase();

    this.filteredCountriesList = this.countries.filter(o =>
      o.name.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method countrySelected
   * @description Handles country selection, updates validators based on country, and loads states.
   * @param {any} country - The selected country object.
   */
  public countrySelected(country: any): void {
    this.addPerson.get('countryName')!.setValue(country.name);
    this.isCountryDropdown = false;
    this.isCountryChanged = true;
    this.countrySearch = country.name;
    this.addPerson.get('personMobileNumber').clearValidators();
    this.addPerson.get('pincode').clearValidators();
    this.getStates();
    this.getDialCode();
    switch (country.name) {
      case 'Spain':
        this.addPerson.get('personMobileNumber').setValidators([Validators.pattern('^[0-9]{9,9}$'), Validators.required]);
        this.addPerson.get('personMobileNumber').updateValueAndValidity();
        this.addPerson.get('pincode').setValidators([Validators.minLength(5), Validators.maxLength(5), Validators.pattern('^[0-9]{5,5}$'), Validators.required]);
        this.addPerson.get('pincode').updateValueAndValidity();
        break;
      case 'Indonesia':
        this.addPerson.get('personMobileNumber').setValidators([Validators.pattern('^[0-9]{5,10}$'), Validators.required]);
        this.addPerson.get('personMobileNumber').updateValueAndValidity();
        this.addPerson.get('pincode').setValidators([Validators.minLength(5), Validators.maxLength(5), Validators.pattern('^[0-9]{5,5}$'), Validators.required]);
        this.addPerson.get('pincode').updateValueAndValidity();
        break;
      case 'Nigeria':
      case 'Vietnam':
        this.addPerson.get('personMobileNumber').setValidators([Validators.pattern('^[0-9]{7,10}$'), Validators.required]);
        this.addPerson.get('personMobileNumber').updateValueAndValidity();
        this.addPerson.get('pincode').setValidators([Validators.minLength(6), Validators.maxLength(6), Validators.pattern('^[0-9]{6,6}$'), Validators.required]);
        this.addPerson.get('pincode').updateValueAndValidity();
        break;
      case 'Malaysia':
        this.addPerson.get('personMobileNumber').setValidators([Validators.pattern('^[0-9]{7,9}$'), Validators.required]);
        this.addPerson.get('personMobileNumber').updateValueAndValidity();
        this.addPerson.get('pincode').setValidators([Validators.minLength(5), Validators.maxLength(5), Validators.pattern('^[0-9]{5,5}$'), Validators.required]);
        this.addPerson.get('pincode').updateValueAndValidity();
        break;
      case 'Australia':
        this.addPerson.get('personMobileNumber').setValidators([Validators.pattern('^[0-9]{5,15}$'), Validators.required]);
        this.addPerson.get('personMobileNumber').updateValueAndValidity();
        this.addPerson.get('pincode').setValidators([Validators.minLength(4), Validators.maxLength(4), Validators.pattern('^[0-9]{4,4}$'), Validators.required]);
        this.addPerson.get('pincode').updateValueAndValidity();
        break;
      case 'Ecuador':
        this.addPerson.get('personMobileNumber').setValidators([Validators.pattern('^[0-9]{8,8}$'), Validators.required]);
        this.addPerson.get('personMobileNumber').updateValueAndValidity();
        this.addPerson.get('pincode').setValidators([Validators.minLength(6), Validators.maxLength(6), Validators.pattern('^[0-9]{6,6}$'), Validators.required]);
        this.addPerson.get('pincode').updateValueAndValidity();
        break;
      case 'Saudi Arabia':
        this.addPerson.get('personMobileNumber').setValidators([Validators.pattern('^[0-9]{8,9}$'), Validators.required]);
        this.addPerson.get('personMobileNumber').updateValueAndValidity()
        this.addPerson.get('pincode').setValidators([Validators.minLength(5), Validators.maxLength(5), Validators.pattern('^[0-9]{5,5}$'), Validators.required]);
        this.addPerson.get('pincode').updateValueAndValidity();
        break;
      default:
        this.addPerson.get('personMobileNumber').setValidators([Validators.pattern('^[0-9]{10,10}$'), Validators.required]);
        this.addPerson.get('personMobileNumber').updateValueAndValidity();
        this.addPerson.get('pincode').setValidators([Validators.minLength(6), Validators.maxLength(6), Validators.pattern('^[0-9]{6,6}$'), Validators.required]);
        this.addPerson.get('pincode').updateValueAndValidity();
        break;
    }
  }

  /**
   * @method filterStates
   * @description Filters the state list based on user input.
   */
  public filterStates(): void {
    const value = (this.addPerson.get('stateName')!.value || '') as StateModel | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.name.toLowerCase();

    this.filteredStatesList = this.states.filter(o =>
      o.name.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method stateSelection
   * @description Handles state selection and loads villages.
   * @param {any} state - The selected state object.
   */
  public stateSelection(state: any): void {
    this.addPerson.get('stateName')!.setValue(state.name);
    this.isStateDropdown = false;
    this.isStateChanged = true;
    this.stateSearch = state.name;
    this.getVillages(state);
  }
  /* State Selection Field end */

  /**
   * @method
   * @returns {Promise<void>} A promise that resolves when the list of states is populated or relevant data is updated.
   * Description:
   * This method Retrieves and populates the list of states based on the selected country, while also handling certain country-specific logic.
   */
  public getStates(): void {
    this.states = [];
    this.villages = [];
    this.filteredStatesList = [];
    this.filteredVillagesList = [];
    if (this.submitButton || (this.updateButton && this.isCountryChanged)) {
      this.addPerson.get('stateName')!.setValue('');
      this.addPerson.get('villageName')!.setValue('');
    }
    const selectedCountryObject = this.countries.find((country: CountriesModel) => country.name == this.countrySearch);
    this.states = selectedCountryObject.states;
    this.filteredStatesList = this.states;
  }

  // ==========================================================================
  // VILLAGE SELECTION LOGIC
  // ==========================================================================

  /**
   * @method getVillages
   * @async
   * @description Retrieves and populates the list of villages based on the selected state.
   * Handles chunked loading for performance.
   * @param {any} state - The selected state object.
   * @returns {Promise<void>}
   * @side-effect Updates `villages`, `visibleVillages`, and loading flags.
   */

  public isLoadingVillages = false;
  public isLoadingMore = false;
  public villagesLoadError = false;
  public visibleVillages: any[] = [];
  public CHUNK_SIZE = 50;
  private currentIndex = 0;
  private searchText = '';
  private isLoadTriggered = false;
  public noDataFound = false;

  public async getVillages(state: any) {
    this.resetVillages();
    this.isLoadingVillages = true;
    this.villagesLoadError = false;
    this.noDataFound = false;

    try {
      const response = await this.statesAPIService.getVillagesByStates(state.name);
      if (response?.[0]?.city_town_village?.length) {
        this.villages = response[0].city_town_village;
        this.loadNextChunk();
      } else {
        this.villagesLoadError = true;
      }
    } catch (err) {
      this.villagesLoadError = true;
    } finally {
      this.isLoadingVillages = false;
    }
  }

  /**
   * @method loadNextChunk
   * @private
   * @description Loads the next chunk of villages into the visible list.
   */
  private loadNextChunk() {
    const source = this.searchText
      ? this.villages.filter(v =>
        v['city_town_village_name'].toLowerCase().includes(this.searchText.toLowerCase())
      )
      : this.villages;

    if (source.length === 0) {
      this.noDataFound = true;
      return;
    }

    this.noDataFound = false; // reset when data exists
    const nextSet = source.slice(this.currentIndex, this.currentIndex + this.CHUNK_SIZE);
    this.visibleVillages = [...this.visibleVillages, ...nextSet];
    this.currentIndex += this.CHUNK_SIZE;
  }

  /**
   * @method onVillageScroll
   * @description Handles scroll events in the village dropdown to load more items.
   */
  public onVillageScroll() {
    if (this.isLoadTriggered || this.isLoadingMore) return;

    const remaining = this.getFilteredList()?.length - this.visibleVillages.length;
    if (remaining > 0) {
      this.isLoadTriggered = true;
      this.isLoadingMore = true;

      setTimeout(() => {
        this.ngZone.run(() => {
          this.loadNextChunk();
          this.isLoadingMore = false;
          this.isLoadTriggered = false;
          this.cdr.detectChanges();
        });
      }, 300);
    }
  }

  /**
   * @method filterVillages
   * @description Filters the village list based on user input.
   * @param {any} event - The input event.
   */
  public filterVillages(event: any) {
    this.searchText = event.target.value;
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.loadNextChunk();
  }

  /**
   * @method getFilteredList
   * @private
   * @description Returns the filtered list of villages based on search text.
   */
  private getFilteredList() {
    return this.searchText
      ? this.villages.filter(v =>
        v['city_town_village_name'].toLowerCase().includes(this.searchText.toLowerCase())
      )
      : this.villages;
  }

  /**
   * @method resetVillages
   * @private
   * @description Resets village data and state.
   */
  private resetVillages() {
    this.villages = [];
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.searchText = '';
    this.isLoadTriggered = false;
    this.noDataFound = false;
    this.addPerson.get('villageName')?.setValue('');
  }

  /**
   * @method onVillageFocus
   * @description Handles focus event on the village input field.
   */
  public onVillageFocus() {
    if (!this.searchText) {
      // If no search text, just reload first chunk
      this.visibleVillages = [];
      this.currentIndex = 0;
      this.loadNextChunk();
    } else {
      // If search exists, reload search result
      this.visibleVillages = [];
      this.currentIndex = 0;
      this.loadNextChunk();
    }
  }

  /**
   * @method onVillageBlur
   * @description Handles blur event on the village input field.
   */
  public onVillageBlur() {
    if (this.noDataFound || this.visibleVillages.length == 0) {
      this.clearVillageField();
    }
  }

  /**
   * @method clearVillageField
   * @private
   * @description Clears the village input field and resets state.
   */
  private clearVillageField() {
    this.addPerson.get('villageName')?.setValue('');
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.searchText = '';
  }

  /**
   * @method villageSelection
   * @description Handles village selection from the dropdown.
   * @param {any} village - The selected village object.
   */
  public villageSelection(village: any): void {
    this.addPerson.get('villageName')!.setValue(village.city_town_village_name);
    this.isVillageDropdown = false;
    this.villageSearch = village.city_town_village_name;
    if (this.villageSearch) {
      this.isVillageChanged = true;
    }
  }

  /**
   * @method skipRegistration
   * @description Skips the registration process and navigates to home.
   */
  public skipRegistration() {
    localStorage.setItem('alertShown', 'true');
    this.BAPersonsAvailable.length = 1;
    $('#notification').modal('hide');
    this.router.navigate(['/home']);
  }

  /**
   * @method BAPersonRegistration
   * @description Opens the modal for Business Admin person registration.
   */
  public BAPersonRegistration() {
    this.BAPersonsAvailable = this.persons.length > 0 ? this.persons.filter(bPersons => ['618a5b620f339c001e991b31'].includes(bPersons && bPersons.personType[0].personRoleID)) : [];
    localStorage.setItem('alertShown', 'true');
    $('#notification').modal('hide');
    $('#addpersonModal').modal({
      backdrop: 'static',
      keyboard: false,
      show: true
    });
    this.submitButton = true;
  }

  /**
   * @method ngOnDestroy
   * @lifecycle angular
   * @description Cleans up resources, unsubscribes from observables, and cancels HTTP requests.
   * @returns {void}
   */
  public ngOnDestroy(): void {
    this.paramSubscription.unsubscribe();
    this.businessTypeService.cancelOnGoingHttpRequests();
    this.personRoleService.cancelOnGoingHttpRequests();
    this.personService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.productOrdersService.cancelOnGoingHttpRequests();
    this.locationTrackerService.cancelOnGoingHttpRequests();
    this.feedlogservice.cancelOnGoingHttpRequests();
    this.treatmlogservice.cancelOnGoingHttpRequests();
    this.observationlogservice.cancelOnGoingHttpRequests();
    this.samplingservice.cancelOnGoingHttpRequests();
    this.harvestlogservice.cancelOnGoingHttpRequests();
    this.farmSiteService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }

  /**
   * @method initDates
   * @description Initializes date variables for filtering (today, last week, last month, last year).
   */
  public initDates() {
    let date = new Date();
    date.setDate(date.getDate() - 7);
    this.weekfromDate = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
    date = new Date();
    date.setDate(date.getDate() - 30);
    this.monthfromDate = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
    date = new Date();
    date.setDate(date.getDate() - 365);
    this.yearfromDate = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
    date = new Date();
    this.todayDate = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
  }

  /**
   * @method getPersonsByBusinessId
   * @async
   * @description Filters the person list based on the selected business ID and person type.
   */
  public async getPersonsByBusinessId() {
    try {
      if (this.businessType) {

        if (this.persontype == 'Provider Technician') {
          const persons = this.loginPersonType == 'EMPLOYEE' ? this.totalPersonsList : this.dupBusinessAdminPersons
          this.personsByBusiness = persons.filter(person => this.businessType == person?.businessID?.id && ['Business Admin', 'Provider Manager'].includes(person?.personType?.[0]?.name));
        }
        else if (this.persontype == 'Provider Manager') {
          const persons = this.loginPersonType == 'EMPLOYEE' ? this.totalPersonsList : this.dupBusinessAdminPersons
          this.personsByBusiness = persons.filter(person => this.businessType == person?.businessID?.id && ['Business Admin'].includes(person?.personType?.[0]?.name));
        }
        else if (this.persontype == 'Farmer') {
          const persons = this.loginUserBusinessCategory == 'Provider' ? this.disptotalBaBusinessPersons : this.totalPersonsList;
          this.personsByBusiness = persons.filter(person => this.businessType == person?.businessID?.id && ['Business Admin'].includes(person?.personType?.[0]?.name));
        }
        else if (this.persontype == 'Farm Manager') {
          const persons = this.loginUserBusinessCategory == 'Provider' ? this.disptotalBaBusinessPersons : this.totalPersonsList;
          this.personsByBusiness = persons.filter(person => this.businessType == person?.businessID?.id && ['Farmer', 'Business Admin'].includes(person?.personType?.[0]?.name));
        }
        else if ((this.persontype == 'Farm Tech' || this.persontype == 'Farm Investor')) {
          const persons = this.loginUserBusinessCategory == 'Provider' ? this.disptotalBaBusinessPersons : this.totalPersonsList;
          this.personsByBusiness = persons.filter(person => this.businessType == person?.businessID?.id && ['Farmer', 'Farm Manager', 'Business Admin'].includes(person?.personType?.[0]?.name));
        }
        else if (this.persontype == 'SME') {
          const persons = this.loginPersonType == 'EMPLOYEE' ? this.totalPersonsList : this.disptotalBaBusinessPersons
          this.personsByBusiness = persons.filter(person => this.businessType == person?.businessID?.id && ['SME'].includes(person?.personType?.[0]?.name));
        }
        else if (this.persontype == 'Buyer') {
          this.personsByBusiness = this.totalPersonsList.filter(person => ['Buyer'].includes(person?.personType?.[0]?.name));
        } else if (this.persontype == 'Dealer') {
          const persons = this.loginPersonType == 'EMPLOYEE' ? this.totalPersonsList : this.disptotalBaBusinessPersons
          this.personsByBusiness = persons.filter(person => this.businessType == person?.businessID?.id && ['Dealer'].includes(person?.personType?.[0]?.name));
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getPersonProfileById
   * @async
   * @description Fetches detailed profile information for a person, including activity logs and location tracking.
   * @param {string} personId - The ID of the person.
   * @param {string} personType - The type of the person (e.g., 'Provider Technician').
   */
  public async getPersonProfileById(personId: string, personType: string) {
    try {
      if (this.paramFieldForce == 'Staff') {
        $('#personProfileModal').modal('show');
      }
      this.responseLoader = false;
      this.getAllLocationTrackers = await this.locationTrackerService.getAllLocationTrackers();
      this.getAllFeedlogActivities = await this.feedlogservice.getAllFeedLogActivities();
      this.getAllTreatmentLogActivities = await this.treatmlogservice.getAllTreatmentLogActivities();
      this.getAllObservationLogActivities = await this.observationlogservice.getAllObservationLogActivities();
      this.getAllSamplingAcytivities = await this.samplingservice.getAllSamplingActivities();
      this.getAllHarvestLogActivities = await this.harvestlogservice.getAllHarvestLogActivities();
      this.getAllFarmsites = await this.farmSiteService.getAllFarmSites();
      this.productOrders = await this.productOrdersService.getProductOrdersByProviderTechnicianID(personId);
      this.responseLoader = true;
      let startDate = new Date(this.monthfromDate);
      this.personProfile = this.disptotalPersonsList.find(person => person && person.id === personId);
      let filteredFeedlogs: Array<FeedLogModel> = [];
      let filtetedTreatments = [];
      let filtetedObservations = [];
      let filtetedHarvestlogs: Array<HarvestLogModel> = [];
      let filtetedSamplinglogs: Array<SamplingModel> = [];
      let farmVisits: any = [];
      let lastMonthTrackers: any;
      if (personType == 'Provider Technician') {
        let latlongsObj = new Array();
        let rad: number = 6371;
        let totalDistance: number = 0;
        if (this.productOrders && this.productOrders.length > 0) {
          this.personProfile.orders = (this.productOrders.filter(order => order && order.generated_by_person_id == personId)).length;
        }
        let business: any = await this.businessEntityService.getTechnicianLeads(this.businessEntity.id, personId);
        if (business) {
          this.personProfile.businessess = business.totalFarms;
        }
        if (this.getAllFeedlogActivities && this.getAllFeedlogActivities.length > 0) {
          let feedlogActivities = this.getAllFeedlogActivities.filter((feedlog: FeedLogModel) => {
            let date = new Date(feedlog.last_feed_given_date);
            return (date >= startDate) && feedlog.person_id == personId && feedlog.location_tracker_id != null;
          });
          if (feedlogActivities.length > 0) {
            filteredFeedlogs = feedlogActivities.filter((element, index) => (feedlogActivities.findIndex(Object => Object.last_feed_given_date == element.last_feed_given_date && Object.farm_site_id == element.farm_site_id) == index));
          }
        }
        if (this.getAllTreatmentLogActivities && this.getAllTreatmentLogActivities.length > 0) {
          let treatmentlogActivities: Array<any> = this.getAllTreatmentLogActivities.filter((treatmentlog) => {
            let date = new Date(treatmentlog.date);
            return (date >= startDate) && treatmentlog.person_id == personId && treatmentlog.location_tracker_id != null;
          });
          if (treatmentlogActivities.length > 0) {
            filtetedTreatments = treatmentlogActivities.filter((element, index) => (treatmentlogActivities.findIndex(Object => Object.date == element.date && Object.farm_site_id == element.farm_site_id) == index));
          }
        }
        if (this.getAllObservationLogActivities && this.getAllObservationLogActivities.length > 0) {
          let observationActivities = this.getAllObservationLogActivities.filter((observationlog) => {
            let date = new Date(observationlog.date);
            return (date >= startDate) && observationlog.person_id == personId && observationlog.location_tracker_id != null;
          });
          if (observationActivities.length > 0) {
            filtetedObservations = observationActivities.filter((element, index) => (observationActivities.findIndex(Object => Object.date == element.date && Object.farm_site_id == element.farm_site_id) == index));
          }
        }
        if (this.getAllSamplingAcytivities && this.getAllSamplingAcytivities.length > 0) {
          let samplingActivities = this.getAllSamplingAcytivities.filter((samplinglog: SamplingModel) => {
            let date = new Date(samplinglog.date);
            return (date >= startDate) && samplinglog.person_id == personId && samplinglog.location_tracker_id != null;
          });
          if (samplingActivities.length > 0) {
            filtetedSamplinglogs = samplingActivities.filter((element, index) => (samplingActivities.findIndex(Object => Object.date == element.date && Object.farm_site_id == element.farm_site_id) == index));
          }
        }
        if (this.getAllHarvestLogActivities && this.getAllHarvestLogActivities.length > 0) {
          let harvestActivities = this.getAllHarvestLogActivities.filter((harvestlog: HarvestLogModel) => {
            let date = new Date(harvestlog.createdAt);
            return (date >= startDate) && harvestlog.person_id == personId && harvestlog.location_tracker_id != null;
          });
          if (harvestActivities.length > 0) {
            filtetedHarvestlogs = harvestActivities.filter((element, index) => (harvestActivities.findIndex(Object => Object.date == element.date && Object.farm_site_id == element.farm_site_id) == index));
          }
        }
        farmVisits = [...(filteredFeedlogs && filteredFeedlogs.length > 0 ? filteredFeedlogs : []), ...(filtetedTreatments && filtetedTreatments.length > 0 ? filtetedTreatments : []), ...(filtetedObservations && filtetedObservations.length > 0 ? filtetedObservations : []), ...(filtetedHarvestlogs && filtetedHarvestlogs.length > 0 ? filtetedHarvestlogs : []), ...(filtetedSamplinglogs && filtetedSamplinglogs.length > 0 ? filtetedSamplinglogs : [])];
        let duplicateFarmVisits = new Array();
        for (let k = 0; k < farmVisits.length; k++) {
          if (farmVisits[k].last_feed_given_date) {
            farmVisits[k].date = moment(new Date(farmVisits[k].last_feed_given_date)).format('YYYY-MM-DD');
          } else {
            farmVisits[k].date = moment(new Date(farmVisits[k].date)).format('YYYY-MM-DD');
          }
          duplicateFarmVisits.push(farmVisits[k]);
        }
        let finalFilteredFarmVisits = duplicateFarmVisits.filter((element, index) => (duplicateFarmVisits.findIndex(Object => Object.date == element.date && Object.farm_site_id == element.farm_site_id) == index));
        this.personProfile.farmvisits = finalFilteredFarmVisits.length > 0 ? finalFilteredFarmVisits.length : 0;
        if (this.getAllLocationTrackers && this.getAllLocationTrackers.length > 0) {
          let locationTrackers = this.getAllLocationTrackers.filter(tracker => tracker && tracker.personID == personId);
          if (locationTrackers.length > 0) {
            lastMonthTrackers = locationTrackers.filter(a => {
              let date = new Date(a.atFarmOnDateTime);
              return (date >= startDate);
            });
            let data = lastMonthTrackers.filter(keys => keys && keys.atFarmOnDateTime && keys.atFarmOffDateTime);
            for (let j = 0; j < data.length; j++) {
              latlongsObj.push({
                'onlatitude': data[j].atFarmOnlatitude,
                'offlatitude': data[j].atFarmOfflatitude,
                'onlongitude': data[j].atFarmOnlongitude,
                'offlongitude': data[j].atFarmOfflongitude
              });
            }
            if (latlongsObj.length > 0) {
              for (let i = 0; i < latlongsObj.length; i++) {
                if (i == 0) {
                  let latitude1: any = latlongsObj[i].onlatitude;
                  let longitude1: any = latlongsObj[i].onlongitude;
                  let latitude2: any = latlongsObj[i].offlatitude;
                  let longitude2: any = latlongsObj[i].offlongitude;
                  if (latitude1 != 0 && longitude1 != 0 && latitude2 != 0 && longitude2 != 0) {
                    let dLat = (latitude2 - latitude1) * Math.PI / 180.0;
                    let dLon = (longitude2 - longitude1) * Math.PI / 180.0;
                    latitude1 = (latitude1) * Math.PI / 180.0;
                    latitude2 = (latitude2) * Math.PI / 180.0;
                    let a = Math.pow(Math.sin(dLat / 2), 2) + Math.pow(Math.sin(dLon / 2), 2) * Math.cos(latitude1) * Math.cos(latitude2);
                    let c = 2 * Math.asin(Math.sqrt(a));
                    totalDistance = totalDistance + (rad * c);
                  }
                } else {
                  let latitude1: any = latlongsObj[i - 1].offlatitude;
                  let longitude1: any = latlongsObj[i - 1].offlongitude;
                  let latitude2: any = latlongsObj[i].onlatitude;
                  let longitude2: any = latlongsObj[i].onlongitude;
                  if (latitude1 != 0 && longitude1 != 0 && latitude2 != 0 && longitude2 != 0) {
                    let dLat = (latitude2 - latitude1) * Math.PI / 180.0;
                    let dLon = (longitude2 - longitude1) * Math.PI / 180.0;
                    latitude1 = (latitude1) * Math.PI / 180.0;
                    latitude2 = (latitude2) * Math.PI / 180.0;
                    let a = Math.pow(Math.sin(dLat / 2), 2) + Math.pow(Math.sin(dLon / 2), 2) * Math.cos(latitude1) * Math.cos(latitude2);
                    let c = 2 * Math.asin(Math.sqrt(a));
                    totalDistance = totalDistance + (rad * c);
                  }
                }
              }
              this.personProfile.totalDistance = totalDistance.toFixed(2);
            }
          } else {
            this.personProfile.totalDistance = 0;
          }
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method f
   * @description Convenience getter for form controls.
   * @returns {AbstractControl} Form controls.
   */
  public get f() {
    return this.addPerson.controls;
  }

  /**
   * @method applySearch
   * @description Filters the person list based on the search input.
   */
  public applySearch(): void {
    this.persons = [];
    this.baBusinessPersons = [];
    const query = this.search?.replace(/[-_]|\s/g, "").trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;

    if (this.isFiltering) {
      if (this.loginUserBusinessCategory == 'Provider') {
        this.baBusinessPersons = this.disptotalBaBusinessPersons.filter(person =>
          person.firstName?.concat(person.lastName)?.replace(/[-_]|\s/g, "").toLowerCase().startsWith(query) ||
          person?.businessID?.businessName?.replace(/[-_]|\s/g, "").toLowerCase().startsWith(query) ||
          person?.personType?.[0]?.name?.replace(/[-_]|\s/g, "").toLowerCase().startsWith(query) ||
          person?.mobileNumber?.startsWith(query) ||
          person?.email?.startsWith(query)
        );
        this.noList = this.baBusinessPersons.length === 0;
      } else {
        this.persons = this.disptotalPersonsList.filter(person =>
          person.firstName?.concat(person.lastName)?.replace(/[-_]|\s/g, "").toLowerCase().startsWith(query) ||
          person?.businessID?.businessName?.replace(/[-_]|\s/g, "").toLowerCase().startsWith(query) ||
          person?.personType?.[0]?.name?.replace(/[-_]|\s/g, "").toLowerCase().startsWith(query) ||
          person?.mobileNumber?.startsWith(query) ||
          person?.email?.startsWith(query)
        );
        this.noList = this.persons.length === 0;
      }
    } else {
      this.initializeBatchLoader();
      this.batchLoader?.reset();
      this.batchLoader?.loadNextBatch(true);
    }
  }

  /**
   * @method compareSites
   * @description Comparator for farm sites.
   * @param {any} a - First site.
   * @param {any} b - Second site.
   * @returns {boolean} True if IDs match.
   */
  public compareSites = (a: any, b: any) => a && b && a.id === b.id;

  /**
   * @method onSiteToggle
   * @description Handles toggle event for farm site selection.
   * @param {any} site - The site object.
   * @param {MatOptionSelectionChange} evt - The selection change event.
   */
  public onSiteToggle(site: any, evt: MatOptionSelectionChange): void {
    if (!evt.isUserInput) return;

    if (evt.source.selected) {
      // Add site uniquely
      if (!this.siteselectedItems.some((s: any) => s.id === site.id)) {
        this.siteselectedItems = [...this.siteselectedItems, site];
      }
    } else {
      // Remove site
      this.siteselectedItems = this.siteselectedItems.filter((s: any) => s.id !== site.id);
    }

    // Sync with reactive form control
    this.addPerson.get('formsiteId')?.setValue(this.siteselectedItems);
  }

  /**
   * @method compareSections
   * @description Comparator for sections.
   * @param {any} a - First section.
   * @param {any} b - Second section.
   * @returns {boolean} True if itemNames match.
   */
  public compareSections = (a: any, b: any) => a && b && a.itemName === b.itemName;

  /**
   * @method onSectionToggle
   * @description Handles toggle event for section selection.
   * @param {any} section - The section object.
   * @param {MatOptionSelectionChange} evt - The selection change event.
   */
  public onSectionToggle(section: any, evt: MatOptionSelectionChange): void {
    if (!evt.isUserInput) { return; }        // Ignore programmatic changes

    if (evt.source.selected) {
      // SELECT
      if (section.alreadyAssigned) {         // replace with your real flag
        const ok = confirm(
          `${section.itemName} already assigned to another tech.\n` +
          `Proceed to move it to this tech?`
        );
        if (!ok) {
          evt.source.deselect();
          return;
        }
      }

      // Add uniquely
      if (!this.sections_selectedItems.some((s: any) => s.itemName === section.itemName)) {
        this.sections_selectedItems = [...this.sections_selectedItems, section];
      }

    } else {
      // DESELECT
      this.sections_selectedItems = this.sections_selectedItems
        .filter((s: any) => s.itemName !== section.itemName);
    }

    // Sync form control if using reactive forms
    this.addPerson.get('sections')?.setValue(this.sections_selectedItems);
  }


  /**
   * @method compareItems
   * @description Comparator for items (ponds).
   * @param {any} a - First item.
   * @param {any} b - Second item.
   * @returns {boolean} True if itemNames match.
   */
  public compareItems = (a: any, b: any) => a && b && a.itemName === b.itemName;

  /**
   * @method onPondToggle
   * @description Handles toggle event for pond selection.
   * @param {any} pond - The pond object.
   * @param {MatOptionSelectionChange} evt - The selection change event.
   */
  public onPondToggle(pond: any, evt: MatOptionSelectionChange): void {
    // Ignore programmatic changes
    if (!evt.isUserInput) { return; }

    if (evt.source.selected) {
      /* ---- User is TRYING to SELECT this pond ---- */

      if (pond.farmInvestorID != null) {
        const confirmMsg =
          `${pond.itemName} is already assigned to another investor.\n` +
          `If you continue they will lose access.\nProceed?`;

        if (!confirm(confirmMsg)) {
          // User cancelled → immediately undo selection
          evt.source.deselect();
          return;
        }
      }

      // Add uniquely
      if (!this.ponds_selectedItems.some((p: any) => p.itemName === pond.itemName)) {
        this.ponds_selectedItems = [...this.ponds_selectedItems, pond];
      }

    } else {
      /* ---- User DESELECTED ---- */
      this.ponds_selectedItems = this.ponds_selectedItems
        .filter((p: any) => p.itemName !== pond.itemName);
    }

    // Sync the reactive-form control if you’re using one:
    this.addPerson.get('pondId')?.setValue(this.ponds_selectedItems);
  }


  /**
   * @method personDetailsSubmit
   * @async
   * @description Handles the submission of the new person form.
   * Validates input, creates person record, creates ACL user, and sends credentials.
   */
  public async personDetailsSubmit() {
    if (this.BAPersonsAvailable.length == 0 && this.businessPersonsCount == 0) {
      const myInputControl = this.addPerson.get('person');
      myInputControl.setValidators(null);
      myInputControl.updateValueAndValidity();
      this.addPerson.get('level').setValidators(null);
      this.addPerson.get('level').updateValueAndValidity();
    } else {
      const myInputControl = this.addPerson.get('person');
      const validators = myInputControl.validator ? [myInputControl.validator, Validators.required] : Validators.required;
      myInputControl.setValidators(validators);
      myInputControl.updateValueAndValidity();
      this.addPerson.get('level').setValidators(Validators.required);
      this.addPerson.get('level').updateValueAndValidity();
    }
    if (['Business Admin'].includes(this.persontype) || (this.paramFieldForce == 'People' && ['Farmer', 'SME', 'Dealer'].includes(this.persontype) && this.loginPersonType != 'EMPLOYEE') || this.businessItem?.businessType?.[0]?.name == 'External') {
      this.addPerson.get('level').setValidators(null);
      this.addPerson.get('level').updateValueAndValidity();
    }
    try {
      this.submitted = true;
      this.addPerson.markAllAsTouched();
      // stop here if form is invalid
      if (this.addPerson.invalid) {
        return;
      }
      this.addPerson.markAsUntouched()
      this.submitted = false;
      this.personCreationDoubleClick = true;
      this.person.businessID = this.businessType;
      if ((this.businessPersonsCount == 0 && this.loginPersonType == 'EMPLOYEE') || (this.BAPersonsAvailable.length == 0 && this.loginPersonType == 'Farmer')) {
        this.persontype = 'Business Admin';
      }
      if ((this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        this.person.businessID = this.loginbusinessID;
      }
      if (this.persontype == 'Provider Technician' || this.persontype == 'Farmer' || (this.persontype == 'Business Admin' && this.loginUserBusinessCategory == 'Farm')) {
        this.person.farmLocations = [];
      } else if (this.farmSites.length == 0) {
        this.person.farmLocations = [];
      } else if (this.persontype == 'Farm Manager') {
        let siteIdsarr = new Array();
        if (this.siteselectedItems && this.siteselectedItems.length > 0) {
          for (let i = 0; i < this.siteselectedItems.length; i++) {
            let eachselectobj = this.siteselectedItems[i];
            let newObj = {
              farmSiteID: eachselectobj.id,
            };
            siteIdsarr.push(newObj);
          }
        }
        this.person.farmLocations = siteIdsarr;
      } else {
        this.person.farmLocations = [
          {
            farmSiteID: this.person.farmSiteId ? this.person.farmSiteId : null
          }
        ];
      }
      let personRole = this.roles.find(role => role && role.name === this.persontype);
      this.person.personType = [
        {
          name: this.persontype,
          personRoleID: personRole.id
        }
      ];
      if (this.person.email) {
        this.person.email = this.person.email.toLowerCase();
      }

      const country = this.countries.find(countryname => countryname && countryname.name == this.countrySearch);
      this.person.dialingCode = String(country.dialingCode[0]);
      this.person.address = [{
        country: this.countrySearch,
        state: this.stateSearch,
        landMark: this.villageSearch.toLowerCase().includes(',') ? this.villageSearch.split(',')[1] : this.villageSearch,
        district: this.villageSearch.toLowerCase().includes(',') ? this.villageSearch.split(',')[2] : this.villageSearch,
        city: this.villageSearch.toLowerCase().includes(',') ? this.villageSearch.split(',')[0] : this.villageSearch,
        houseNo: this.address.houseNo,
        street: this.address.street,
        pincode: this.person.pincode,
      }];
      if (this.persontype == 'Business Admin' && this.businessItem.businessCategory[0].businessCategoryID == '61890be727c16c001eb0d9a6') {
        this.person.level = 1;
        this.person.reporting = 'self';
      } else if (this.persontype == 'Provider Manager' && this.businessItem.businessCategory[0].businessCategoryID == '61890be727c16c001eb0d9a6') {
        this.person.level = 2;
      } else if (this.persontype == 'Provider Technician' && this.businessItem.businessCategory[0].businessCategoryID == '61890be727c16c001eb0d9a6') {
        this.person.level = 3;
      }
      this.dialingCode = this.person.dialingCode;
      const person = await this.personService.addPerson(this.person);
      this.submitted = false;
      this.addPerson.markAsUntouched();
      if (person) {
        if (this.persontype == 'Farm Tech') {
          this.assignPersonToSection(person.id);
        }
        if (this.persontype == 'Farm Investor') {
          this.assignPersonToPond(person.id);
        }
        if (person) {
          this.passowrdGenerated = await this.personService.passowrdGeneration();
          if (this.passowrdGenerated) {
            this.addUserToAcl = {
              userName: person.mobileNumber,
              password: this.passowrdGenerated.password,
              role: person.personType[0].name,
              businessID: person.businessID,
              personID: person.id
            };
          }
          this.addUsertoacl = await this.personService.addUserToAcl(this.addUserToAcl);
          if (this.addUsertoacl != 'User not created') {
            this.sendPwdTouser = {
              mobileNumber: person.mobileNumber,
              dialingCode: String(this.dialingCode),
              email: person.email,
              password: this.addUserToAcl.password,
              businessID: person.businessID,
              templateNameCode: 'WELCOME_BLUBAY'
            };
            this.passowrdSent = await this.personService.sendUserPwdToMobileEmail(this.sendPwdTouser);
            if (this.passowrdSent) {
              this.toasterService.success('Person created successfully', '', {
                timeOut: 2000
              });
              if (person.personType[0].personRoleID == '618a5b620f339c001e991b31' && this.BAPersonsAvailable.length == 0 && this.loginPersonType == 'Farmer') {
                this.router.navigate(['/home']);
              }
            }
          } else {
            await this.personService.deleteOneByID(this.person.id);
          }
        }
        this.isFiltering = false;
        this.showForm = false;
        this.country = undefined;
        this.businessItem = undefined;
        this.countries = [];
        this.person.email = undefined;
        this.person.employeeDesignation = undefined;
        this.person.employeeCode = undefined;
        this.person.reportingTo = undefined;
        this.address.country = '';
        this.isStateChanged = false;
        this.isCountryChanged = false;
        this.isVillageChanged = false;
        if (this.loginPersonType == 'EMPLOYEE') {
          this.person.firstName = '';
          this.person.lastName = '';
          this.person.mobileNumber = '';
          this.persontype = '';
          this.businessType = '';
          this.person.farmSiteId = undefined;
          this.person.pondId = undefined;
          this.person.pincode = undefined;
          this.businessEntities = new Array();
          this.farmSites = new Array();
          this.getPondsDetailsData = new Array();
        } else if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
          this.person.firstName = '';
          this.person.lastName = '';
          this.person.mobileNumber = '';
          this.persontype = '';
          this.person.farmSiteId = '';
          this.person.pondId = undefined;
          this.person.pincode = undefined;
        } else if ((this.loginPersonType == 'Business Admin' || this.loginPersonType == 'Provider Manager') && this.loginUserBusinessCategory == 'Provider') {
          this.person.firstName = '';
          this.person.lastName = '';
          this.person.mobileNumber = '';
          this.persontype = '';
          this.businessType = '';
          this.person.farmSiteId = undefined;
          this.person.pondId = undefined;
          this.person.email = undefined;
          this.person.pincode = undefined;
        }
      }
      await this.onInit();
      this.closeForm();
      this.person = new PersonsModel();
      this.address = new IAddress();
      this.personType = new IPersonType();
      this.farmLocation = new IFarmLocations();
      this.submitted = false;
    } catch (error) {
      if (error == 'Input already exist!') {
        this.toasterService.error("Mobile number already exists");
      } else {
        this.toasterService.error(error);
      }
      this.personCreationDoubleClick = false;
    }
  }

  /**
   * @method getPersonRolesOnBusinessId
   * @async
   * @description Fetches available person roles based on the selected business entity.
   * @param {BusinessEntity} business - The selected business entity.
   */
  public async getPersonRolesOnBusinessId(business: BusinessEntity) {
    let businessPersons: any;
    this.personRoles = [];
    if (this.loginPersonType == 'EMPLOYEE') {
      businessPersons = this.totalPersonsList.filter(person => person?.businessID?.id == this.businessType);
    } else if (this.loginPersonType == 'Business Admin' || this.loginPersonType == 'Provider Manager' && this.loginUserBusinessCategory == 'Provider') {
      this.BAPersonsAvailable.length = 1;
      const baBusinessPersons = this.disptotalBaBusinessPersons.filter(person => person.businessID?.id == business.id);
      businessPersons = this.paramFieldForce == 'Staff' ? this.businessAdminPersons.length ? this.businessAdminPersons : [1] : baBusinessPersons;
    }
    const myInputControl = this.addPerson.get('person');
    this.businessPersonsCount = ['SME', 'Buyer'].includes(business && business.businessType[0].name) ? 1 : businessPersons.length;
    if (this.BAPersonsAvailable.length == 0 && (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager')) {
      this.businessPersonsCount = 0;
    }
    if (businessPersons.length > 0 || (businessPersons.length == 0 && (['SME', 'Buyer'].includes(business.businessType[0].name) || this.paramFieldForce == 'People'))) {
      const validators = myInputControl.validator ? [myInputControl.validator, Validators.required] : Validators.required;
      myInputControl.setValidators(validators);
      myInputControl.updateValueAndValidity();
      if (this.roles && this.roles.length > 0) {
        for (let i = 0; i < this.roles.length; i++) {
          let personRoleIds = new Array();
          if (this.roles[i].businessType && this.roles[i].businessType.length > 0) {
            for (let j = 0; j < this.roles[i].businessType.length; j++) {
              personRoleIds.push(this.roles[i].businessType[j].businessTypeID);
            }
            if (personRoleIds.includes(business.businessType[0].businessTypeID)) {
              this.personRoles.push(this.roles[i]);
            }
          }
        }
      }
      if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider' && this.paramFieldForce == 'Staff') {
        this.personRoles = this.personRoles.filter(personRole => !['618a5b620f339c001e991b31', '61712f64e37ea5001eb52ab6', '61712f8ce37ea5001eb52ab7'].includes(personRole.id));
      } else if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider' && this.paramFieldForce == 'People') {
        if (business?.businessType?.[0]?.name === 'Farm') {
          this.personRoles = this.personRoles.filter(personRole => ['Farmer'].includes(personRole.name));
        } else if (business?.businessType?.[0]?.name === 'SME') {
          this.personRoles = this.personRoles.filter(personRole => ['SME'].includes(personRole.name));
        } else if (business?.businessType?.[0]?.name === 'Dealer') {
          this.personRoles = this.personRoles.filter(personRole => ['Dealer'].includes(personRole.name));
        }
      } else if (this.loginPersonType == 'Provider Manager' && this.loginUserBusinessCategory == 'Provider' && this.paramFieldForce == 'Staff') {
        this.personRoles = this.personRoles.filter(personRole => ['61712face37ea5001eb52ab8'].includes(personRole.id));
      } else if (this.loginPersonType == 'EMPLOYEE') {
        this.personRoles = this.personRoles.filter(personRole => !['618a5b620f339c001e991b31'].includes(personRole.id));
      }
    } else {
      myInputControl.setValidators(null);
      myInputControl.updateValueAndValidity();
      this.personRoles = this.roles.filter(personRole => personRole.id == '618a5b620f339c001e991b31');
    }
  }

  /**
   * @method personRoleOnSelect
   * @async
   * @description Handles selection of a person role. Triggers loading of farm sites if applicable.
   * @param {string} persontype - The selected person type.
   */
  public async personRoleOnSelect(persontype: string) {
    this.person.farmSiteId = undefined;
    this.getPersonsByBusinessId();
    if (this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Farmer' || this.loginPersonType == 'EMPLOYEE' || this.loginPersonType == 'Business Admin' || this.loginPersonType == 'Provider Manager') {
      if (persontype == 'Farm Tech' || persontype == 'Farm Investor' || persontype == 'Farm Manager') {
        await this.getFarmsites();
      }
    }
  }

  /**
   * @method getBussinessByLoginBusinessIDFarmer
   * @async
   * @description Fetches businesses for Provider Technician login when selecting Farmer role.
   */
  public async getBussinessByLoginBusinessIDFarmer() {
    try {
      this.businessEntities = await this.businessEntityService.getAllfarmerByProviderID(this.loginbusinessID);
      this.businessEntity = JSON.parse(localStorage.getItem('loggedInBusiness'));
      if (this.businessEntity) {
        this.businessEntities.push(this.businessEntity);
      }
      this.businessType = '';
      this.businessItem = undefined;
      this.getPersonsByBusinessId();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getFarmsites
   * @async
   * @description Fetches farm sites based on the selected business and person type.
   */
  public async getFarmsites() {
    try {
      // empty the dropdowns
      this.farmSites = new Array();
      this.getPondsDetailsData = new Array();
      this.person.pondId = undefined;
      // empty the dropdowns
      if ((!this.businessType || ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm')) && this.persontype == '') {
        return;
      }
      // if person tupe is not equal to Farmer  and providertechnical
      if (this.loginPersonType != 'Business Admin' && this.loginPersonType != 'Farm Manager') {
        if (this.persontype != 'Farmer' && this.persontype != 'Provider Technician') {
          this.farmSites = await this.farmSiteService.getFarmeSitesByFarmsId(this.businessType);
          this.sitedropdownList = new Array();
          if (this.farmSites && this.farmSites.length > 0) {
            for (let i = 0; i < this.farmSites.length; i++) {
              let eachrec = this.farmSites[i];
              let newobj = { id: eachrec.id, itemName: eachrec.farmNameAtLocation };
              this.sitedropdownList.push(newobj);
            }
          }
          this.prepareSiteSelectedItems();
        }
      } else if (this.loginPersonType != 'Business Admin' && this.loginPersonType == 'Farm Manager') {
        if (this.persontype != 'Farmer' && this.persontype != 'Provider Technician') {
          this.farmSites = await this.farmSiteService.getFarmeSitesByFarmManagerIdInFarmSpend(this.businessType, localStorage.personID);
          this.sitedropdownList = new Array();
          if (this.farmSites && this.farmSites.length > 0) {
            for (let i = 0; i < this.farmSites.length; i++) {
              let eachrec = this.farmSites[i];
              let newobj = { id: eachrec.id, itemName: eachrec.farmNameAtLocation };
              this.sitedropdownList.push(newobj);
            }
          }
          this.prepareSiteSelectedItems();
        }
      } else {
        if (this.persontype != 'Farmer' && this.businessType) {
          this.farmSites = await this.farmSiteService.getFarmeSitesByFarmsId(this.businessType);
          this.sitedropdownList = new Array();
          if (this.farmSites && this.farmSites.length > 0) {
            for (let i = 0; i < this.farmSites.length; i++) {
              let eachrec = this.farmSites[i];
              let newobj = { id: eachrec.id, itemName: eachrec.farmNameAtLocation };
              this.sitedropdownList.push(newobj);
            }
          }
          this.prepareSiteSelectedItems();
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getPondsDetails
   * @async
   * @description Fetches sections and ponds based on the selected farm site ID.
   */
  public async getPondsDetails() {
    try {
      this.sections_dropdownList = new Array();
      this.ponds_dropdownList = new Array();
      this.sections_selectedItems = new Array();
      this.ponds_selectedItems = new Array();
      if (this.persontype != 'Farm Investor') {
        this.pondSections = await this.pondSectionService.getSectionsByFarmsiteId(this.person.farmSiteId);
        this.getPondsDetailsData = this.pondSections;
        this.sections_dropdownList = new Array();
        if (this.getPondsDetailsData.length > 0) {
          for (let i = 0; i < this.getPondsDetailsData.length; i++) {
            let tempobj = {
              'id': this.getPondsDetailsData[i].id,
              'itemName': this.getPondsDetailsData[i].section_name,
              'isAssigned': this.getPondsDetailsData[i].isAssigned
            };
            this.sections_dropdownList.push(tempobj);
          }
        }
      }
      if (this.persontype == 'Farm Investor') {
        this.customerPonds = await this.customerPondService.getAllPondDetailsByFarmSiteId(this.person.farmSiteId);
        this.getPondsDetailsData = this.customerPonds;
        this.ponds_dropdownList = new Array();
        if (this.customerPonds.length > 0) {
          for (let i = 0; i < this.customerPonds.length; i++) {
            let tempobj = {
              'id': this.customerPonds[i].id,
              'itemName': this.customerPonds[i].name,
              'farmInvestorID': this.customerPonds[i].farm_investor_id
            };
            this.ponds_dropdownList.push(tempobj);
          }
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method assignPersonToSection
   * @async
   * @description Assigns a person to selected sections.
   * @param {string} personID - The ID of the person.
   */
  public async assignPersonToSection(personID: string) {
    let inputIds = new Array();
    if (this.sections_selectedItems) {
      for (let i = 0; i < this.sections_selectedItems.length; i++) {
        inputIds.push(this.sections_selectedItems[i].id);
      }
    }
    try {
      this.pondSection = await this.pondSectionService.assignPersonToSection(personID, inputIds);
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method assignPersonToPond
   * @async
   * @description Assigns a person to selected ponds.
   * @param {string} personID - The ID of the person.
   */
  public async assignPersonToPond(personID: string) {
    let inputIds = new Array();
    if (this.ponds_selectedItems) {
      for (let i = 0; i < this.ponds_selectedItems.length; i++) {
        inputIds.push(this.ponds_selectedItems[i].id);
      }
    } else {
      inputIds = new Array();
    }
    try {
      this.customerPond = await this.customerPondService.assignPersonToPond(personID, inputIds);
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method addAddress
   * @description Adds a new address object to the person's address list.
   */
  public addAddress() {
    this.person.address.push({
      houseNo: '',
      street: '',
      landMark: '',
      city: '',
      state: '',
      district: '',
      pincode: '',
      country: ''
    });
  }

  /**
   * @method makeMultiSelctionDropDownUpdate
   * @description Configures settings for multi-select dropdowns during update.
   */

  public makeMultiSelctionDropDownUpdate() {
    this.sections_dropdownSettings = {
      singleSelection: false,
      text: 'Select Ponds',
      selectAllText: 'Select All',
      unSelectAllText: 'UnSelect All',
      // enableSearchFilter: true,
      classes: 'myclass custom-class'
    };
  }

  /**
   * @method makePondsMultiSelctionDropDownUpdate
   * @description Configures settings for pond multi-select dropdowns during update.
   */
  public makePondsMultiSelctionDropDownUpdate() {
    this.ponds_dropdownSettings = {
      singleSelection: false,
      text: 'Select Ponds',
      selectAllText: 'Select All',
      unSelectAllText: 'UnSelect All',
      // enableSearchFilter: true,
      classes: 'myclass custom-class'
    };
  }

  /**
   * @method personUpdateDetailsSubmit
   * @async
   * @description Handles the submission of the person update form.
   */
  public async personUpdateDetailsSubmit() {
    try {
      if (['Business Admin'].includes(this.persontype) || (this.paramFieldForce == 'People' && ['Farmer', 'SME', 'Dealer'].includes(this.persontype) && this.loginPersonType != 'EMPLOYEE') || this.businessItem?.businessType?.[0]?.name == 'External') {
        this.addPerson.get('level').setValidators(null);
        this.addPerson.get('level').updateValueAndValidity();
      }

      this.submitted = true;
      this.addPerson.markAllAsTouched();
      // stop here if form is invalid
      if (this.addPerson.invalid) {
        return;
      }

      this.submitted = false;
      this.personCreationDoubleClick = true;
      if (this.businessType) {
        this.inputbusinessID = this.businessType;
      } else {
        this.inputbusinessID = '';
      }
      this.person.businessID = this.inputbusinessID;
      let personRole = this.personRoles.find(role => role && role.name === this.persontype);
      this.person.personType = [
        {
          name: this.persontype,
          _id: this.personTypeID,
          personRoleID: personRole.id
        }
      ];
      if (this.persontype == 'Provider Technician' || this.persontype == 'Farmer' || (this.persontype == 'Business Admin' && this.loginUserBusinessCategory == 'Farm')) {
        this.person.farmLocations = [];
      } else if (this.farmSites.length == 0) {
        this.person.farmLocations = [];
      } else if (this.persontype == 'Farm Manager') {
        let siteIdsarr = new Array();
        if (this.siteselectedItems && this.siteselectedItems.length > 0) {
          for (let i = 0; i < this.siteselectedItems.length; i++) {
            let eachselectobj = this.siteselectedItems[i];
            let newObj = {
              farmSiteID: eachselectobj.id,
            };
            siteIdsarr.push(newObj);
          }
        }
        this.person.farmLocations = siteIdsarr;
      } else if (this.persontype == 'Farm Tech') {
        this.person.farmLocations = [
          {
            farmSiteID: this.person.farmSiteId ? this.person.farmSiteId : null
          },
          {
            chatUser: this.chatUser ? this.chatUser : true,
            /*_id: this.id ? this.id : ''*/
          }
        ];
      } else {
        this.person.farmLocations = [
          {
            farmSiteID: this.person.farmSiteId ? this.person.farmSiteId : null
          }
        ];
      }
      let typeOfPerson = this.persontype;
      if (this.person.email) {
        this.person.email = this.person.email.toLowerCase();
      }
      let personId = this.person.id;
      const country = this.countries.find(countryname => countryname && countryname.name == this.countrySearch);
      this.person.dialingCode = String(country.dialingCode[0]);
      this.person.address = [{
        country: this.countrySearch,
        state: this.stateSearch,
        landMark: this.villageSearch.toLowerCase().includes(',') ? this.villageSearch.split(',')[1] : this.villageSearch,
        district: this.villageSearch.toLowerCase().includes(',') ? this.villageSearch.split(',')[2] : this.villageSearch,
        city: this.villageSearch.toLowerCase().includes(',') ? this.villageSearch.split(',')[0] : this.villageSearch,
        houseNo: this.address.houseNo,
        street: this.address.street,
        pincode: this.person.pincode,
      }];
      if (this.persontype == 'Business Admin' && this.businessItem.businessCategory[0].businessCategoryID == '61890be727c16c001eb0d9a6') {
        this.person.level = 1;
        this.person.reporting = 'self';
      } else if (this.persontype == 'Provider Manager' && this.businessItem.businessCategory[0].businessCategoryID == '61890be727c16c001eb0d9a6') {
        this.person.level = 2;
      } else if (this.persontype == 'Provider Technician' && this.businessItem.businessCategory[0].businessCategoryID == '61890be727c16c001eb0d9a6') {
        this.person.level = 3;
      }
      const updatePerson = Object.assign({}, this.person);
      const person = this.person;
      delete updatePerson.id;
      let personUpdated = await this.personService.updateOneByID(this.person.id, updatePerson);
      if (personUpdated) {
        this.personRoleDisabled = false;
        if (updatePerson.personType[0].name == 'Provider Technician') {
          let technicianOrders = await this.productOrdersService.getProductOrdersByProviderTechnicianID(person.id);
          let technicianVisits = await this.providerVisitsStatsService.getVisitStatsByTechnicianID(this.person.id);
          let productsPlacedRecords: any[] = await this.productPlacementsService.getAllProductsPlacedByTechnicianID(this.person.id)
          let managerPerson = await this.personService.getOneByID(person.reportingTo);
          if (technicianOrders?.length > 0 && managerPerson) {
            for (let i = 0; i < technicianOrders.length; i++) {
              technicianOrders[i].managed_by_person_id = managerPerson.id;
              technicianOrders[i].managed_by_person_name = managerPerson.firstName + managerPerson.lastName;
              await this.productOrdersService.updateOneByID(technicianOrders[i].id, technicianOrders[i]);
            }
          }
          if (technicianVisits?.length > 0 && managerPerson) {
            for (let i = 0; i < technicianVisits.length; i++) {
              technicianVisits[i].manager_id = managerPerson.id;
              technicianVisits[i].manager_name = managerPerson.firstName + managerPerson.lastName;
              await this.providerVisitsStatsService.updateOneByID(technicianVisits[i].id, technicianVisits[i]);
            }
          }
          if (productsPlacedRecords?.length > 0 && managerPerson) {
            for (let i = 0; i < productsPlacedRecords.length; i++) {
              productsPlacedRecords[i].managed_by_person_id = managerPerson.id;
              productsPlacedRecords[i].managed_by_person_name = managerPerson.firstName + managerPerson.lastName;
              await this.productPlacementsService.updateOneByID(productsPlacedRecords[i].id, productsPlacedRecords[i]);
            }
          }
        }
      }
      this.toasterService.success('Person updated successfully', '', {
        timeOut: 2000
      });
      if (this.loginPersonType == updatePerson.personType[0].name && this.loginPersonMobileNumber != updatePerson.mobileNumber && this.personNumber == this.loginPersonMobileNumber) {
        try {
          this.showForm = false;
          this.sessionModel = await this.sessionService.sessionLogout(localStorage.userID);
          localStorage.closeForm();
          this.router.navigate(['/login']);
        } catch (error) {
          this.toasterService.error(this.sessionModel.message);
        }
      } else {
        this.showForm = false;
        this.isFiltering = false;
        this.person = new PersonsModel();
        this.submitted = false;
        this.country = undefined;
        this.address.country = '';
        if (typeOfPerson == 'Farm Tech') {
          await this.assignPersonToSection(personId);
          this.person = new PersonsModel();
        }
        if (typeOfPerson == 'Farm Investor') {
          await this.assignPersonToPond(personId);
          this.person = new PersonsModel();
        }
        this.closeForm();
        await this.onInit();
      }
    } catch (error) {
      if (error == 'Input already exist!') {
        this.toasterService.error("Mobile number already exists");
      }
      this.personCreationDoubleClick = false;
    }
  }

  /**
   * @method setPersonDetailsInOnLoad
   * @async
   * @description Loads person details into the form for editing.
   * @param {string} id - The ID of the person to edit.
   */
  public async setPersonDetailsInOnLoad(id) {
    this.showForm = true;
    try {
      if (this.loginPersonType == 'Farmer' || (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Farm')) {
        this.farmerDisplay = true;
      }
      this.personCreationDoubleClick = false;
      this.updateButton = true;
      this.submitButton = false;
      this.person = new PersonsModel();
      this.personRoles = new Array();
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        this.businessPersonsCount = 0;
        this.BAPersonsAvailable.length = 1;
      }
      this.person = await this.personService.personDetailsById(id);
      if (this.person) {
        this.businessItem = this.businessEntities.find(business => business && business.id == this.person.businessID.id);
        this.personNumber = this.person.mobileNumber;
        if (this.person.personType && this.person.personType[0]) {
          this.businessType = this.person?.businessID?.id;
          this.businessItem = this.person.businessID;
          this.addPerson.get('businessType')!.setValue(this.person.businessID.businessName);
          this.persontype = this.person.personType[0].name;
          this.personTypeID = this.person.personType[0].personRoleID;
          if (this.persontype != 'Business Admin') {
            this.getPersonRolesOnBusinessId(this.person.businessID);
          } else {
            this.personRoles = this.roles.filter(personRole => personRole.id == this.person.personType?.[0]?.personRoleID);
            this.businessDisabled = true;
            const myInputControl = this.addPerson.get('person');
            myInputControl.setValidators(null);
            myInputControl.updateValueAndValidity();
          }
        }
        if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Farm') {
          if (this.persontype == 'Business Admin') {
            this.personRoleDisabled = true;
            this.personRoles = this.roles.filter(personRole => personRole.id == this.personTypeID);
          } else {
            this.personRoleDisabled = false;
            this.personRoles = this.roles.filter(personRole => ['Farmer', 'Farm Manager', 'Farm Tech', 'Farm Investor'].includes(personRole.name));
          }
        }
        if (this.loginPersonType == 'Farmer' && this.loginUserBusinessCategory == 'Farm') {
          if (this.persontype == 'Farmer') {
            this.personRoleDisabled = true;
            this.personRoles = this.roles.filter(personRole => personRole.id == this.personTypeID);
          } else {
            this.personRoleDisabled = false;
            this.personRoles = this.roles.filter(personRole => ['Farm Manager', 'Farm Tech', 'Farm Investor'].includes(personRole.name));
          }
        }
        if (this.loginPersonType == 'Farm Manager' && this.loginUserBusinessCategory == 'Farm') {
          if (this.persontype == 'Farm Manager') {
            this.personRoleDisabled = true;
            this.personRoles = this.roles.filter(personRole => personRole.id == this.personTypeID);
          } else {
            this.personRoleDisabled = false;
            this.personRoles = this.roles.filter(personRole => ['Farm Tech', 'Farm Investor'].includes(personRole.name));
          }
        }
        this.person.farmSiteId = this.person.farmLocations && this.person.farmLocations[0] && this.person.farmLocations[0].farmSiteID;
        if (this.person.farmLocations[1]) {
          this.chatUser = this.person.farmLocations[1]['chatUser'];
          // this.id = this.person.farmLocations[1]['_id'];
        } else {
          this.chatUser = true;
        }
        if (this.businessType) {
          await this.getFarmsites();
        }
        if (this.person.address.length > 0) {
          this.address = this.person.address[0];
          const selectedCountryObject = this.countries.find((country: CountriesModel) => country.name == this.address.country);
          this.countrySearch = this.address.country;
          this.addPerson.get('countryName')!.setValue(this.countrySearch);
          this.getStates();
          this.stateSearch = this.address.state;
          this.addPerson.get('stateName')!.setValue(this.stateSearch);
          const stateObj = selectedCountryObject.states.find((state: StateModel) => state.name == this.address.state);
          this.getVillages(stateObj);
          this.villageSearch = this.address.city + ',' + this.address.landMark + ',' + this.address.district;
          this.addPerson.get('villageName')!.setValue(this.villageSearch);
          this.person.pincode = this.address.pincode;
        }
        if (this.person.farmSiteId) {
          await this.makesections_dropdownListupdate(this.person.farmSiteId, this.person.id, this.persontype);
        }
        await this.getPersonsByBusinessId();
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method setProviderPersonsDetailsOnLoad
   * @async
   * @description Loads provider person details into the form for editing.
   * @param {string} id - The ID of the person.
   */
  public async setProviderPersonsDetailsOnLoad(id: string) {
    this.showForm = true;
    try {
      this.personCreationDoubleClick = false;
      this.updateButton = true;
      this.submitButton = false;
      this.person = new PersonsModel();
      this.personRoles = new Array();
      if ((this.loginPersonType == 'Business Admin' || this.loginPersonType == 'Provider Manager') && this.loginUserBusinessCategory == 'Provider') {
        this.businessPersonsCount = 0;
        this.BAPersonsAvailable.length = 1;
      }
      const foundPerson = this.dupBusinessAdminPersons.find(person => person?.id === id);
      this.person = foundPerson ? { ...foundPerson } : new PersonsModel(); // clone object
      if (this.person) {
        if (this.person.personType && this.person.personType[0]) {
          this.businessType = this.person.businessID && this.person.businessID.id;
          this.businessItem = this.businessEntity;
          this.addPerson.get('businessType')!.setValue(this.person.businessID.businessName);
          this.persontype = this.person.personType[0].name;
          this.personTypeID = this.person.personType[0]._id;
          if (this.persontype != 'Business Admin') {
            this.getPersonRolesOnBusinessId(this.person.businessID);
          } else {
            this.personRoles = this.roles.filter(personRole => personRole.id == this.person.personType?.[0]?.personRoleID);
            this.businessDisabled = true;
            const myInputControl = this.addPerson.get('person');
            myInputControl.setValidators(null);
            myInputControl.updateValueAndValidity();
          }
        }
        this.personNumber = this.person.mobileNumber;
        if (this.person.address.length > 0) {
          this.address = this.person.address[0];
          const selectedCountryObject = this.countries.find((country: CountriesModel) => country.name == this.address.country);
          this.countrySearch = this.address.country;
          this.addPerson.get('countryName')!.setValue(this.countrySearch);
          this.getStates();
          this.stateSearch = this.address.state;
          this.addPerson.get('stateName')!.setValue(this.stateSearch);
          const stateObj = selectedCountryObject.states.find((state: StateModel) => state.name == this.address.state);
          this.getVillages(stateObj);
          this.villageSearch = this.address.city + ',' + this.address.landMark + ',' + this.address.district;
          this.addPerson.get('villageName')!.setValue(this.villageSearch);
          this.person.pincode = this.address.pincode;
        }
        await this.getPersonsByBusinessId();
        this.person.reportingTo = this.person.reportingTo && this.person.reportingTo.id ? this.person.reportingTo.id : undefined;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method prepareSiteSelectedItems
   * @description Prepares the selected items for the site dropdown during update.
   */
  public prepareSiteSelectedItems() {
    if (this.person && this.person.farmLocations.length > 0) {
      const idsList = _.map(this.person.farmLocations, 'farmSiteID'); // [4,5]
      // tick already selected items
      this.siteselectedItems = new Array();
      for (let i = 0; i < idsList.length; i++) {
        for (let j = 0; j < this.sitedropdownList.length; j++) {
          if (idsList[i] == this.sitedropdownList[j].id) {
            const tempobj = {
              'id': this.sitedropdownList[j].id,
              'itemName': this.sitedropdownList[j].itemName,
            };
            this.siteselectedItems.push(tempobj);
          }
        }
      } // end tick
    }
  }

  /**
   * @method getAllBusinessDetails
   * @async
   * @description Fetches details for a specific business entity.
   * @param {string} id - The business ID.
   */
  public async getAllBusinessDetails(id) {
    try {
      this.businessEntity = await this.businessEntityService.getOneByID(id);
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method makesections_dropdownListupdate
   * @async
   * @description Prepares section and pond dropdown lists for update based on farm site and person.
   * @param {string} farmsiteId - The farm site ID.
   * @param {string} personId - The person ID.
   * @param {string} persontype - The person type.
   */
  public async makesections_dropdownListupdate(farmsiteId: string, personId: string, persontype: string) {
    try {
      this.sections_dropdownList = new Array();
      this.sections_selectedItems = new Array();
      this.ponds_dropdownList = new Array();
      this.ponds_selectedItems = new Array();
      this.totalsectiondropdowns = new Array();
      this.totalpondsdropdowns = new Array();
      let idsList = new Array();
      if (persontype != 'Farm Investor') {
        this.pondSections = await this.pondSectionService.getSectionsByFarmsiteId(farmsiteId);
        this.totalsectiondropdowns = this.pondSections;
        for (let i = 0; i < this.pondSections.length; i++) {
          const tempobj = {
            'id': this.pondSections[i].id,
            'itemName': this.pondSections[i].section_name,
            'isAssigned': this.pondSections[i].isAssigned
          };
          this.sections_dropdownList.push(tempobj);
        }
        if (this.totalsectiondropdowns && this.totalsectiondropdowns.length > 0) {
          idsList = this.totalsectiondropdowns.filter(obj => obj && obj.person_id && obj.person_id == personId);
        }
        for (let i = 0; i < idsList.length; i++) {
          let tempobj = {
            'id': idsList[i].id,
            'itemName': idsList[i].section_name,
            'isAssigned': idsList[i].isAssigned
          };
          this.sections_selectedItems.push(tempobj);
        }
      }
      if (persontype == 'Farm Investor') {
        this.customerPonds = await this.customerPondService.getAllPondDetailsByFarmSiteId(this.person.farmSiteId);
        this.totalpondsdropdowns = this.customerPonds;
        for (let i = 0; i < this.customerPonds.length; i++) {
          const tempobj = {
            'id': this.customerPonds[i].id,
            'itemName': this.customerPonds[i].name,
            'farmInvestorID': this.customerPonds[i].farm_investor_id
          };
          this.ponds_dropdownList.push(tempobj);
        }
        if (this.totalpondsdropdowns && this.totalpondsdropdowns.length > 0) {
          idsList = this.totalpondsdropdowns.filter(obj => obj && obj.farm_investor_id && obj.farm_investor_id == personId);
        }
        for (let i = 0; i < idsList.length; i++) {
          let tempobj = {
            'id': idsList[i].id,
            'itemName': idsList[i].name,
            'farmInvestorID': idsList[i].farm_investor_id
          };
          this.ponds_selectedItems.push(tempobj);
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getFarmsitesUpdate
   * @async
   * @description Fetches farm sites for update based on the selected business.
   */
  public async getFarmsitesUpdate() {
    // empty the dropdowns
    try {
      this.farmSites = new Array();
      this.getPondsDetailsData = [];
      this.person.farmSiteId = undefined;
      this.person.pondId = undefined;
      // empty the dropdowns
      // if person tupe is not equal to Farmer  and providertechnical
      if (this.person.personType[0].name != 'Farmer' && this.person.personType[0].name != 'Provider Technician' && this.person.personType[0].name != 'Farm Manager') {
        this.farmSites = await this.farmSiteService.getFarmeSitesByFarmsId(this.businessType);
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }


  /**
   * @method closeForm
   * @async
   * @description Closes the add/edit person form and resets state.
   */
  public async closeForm() {
    this.addPerson.reset();
    this.showForm = false;
    this.businessPersonsCount = 0;
    if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
      this.BAPersonsAvailable.length = 1;
    }
    if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
      this.getPersonRolesOnBusinessId(this.businessItem);
    }
    this.person = new PersonsModel();
    this.address = new IAddress();
    this.country = undefined;
    this.farmSites = [];
    this.siteselectedItems = [];
    this.sections_selectedItems = [];
    this.ponds_selectedItems = [];
    this.personsByBusiness = [];
    this.villagesList = [];
    this.persontype = undefined;
    this.personNumber = undefined;
    if (this.loginPersonType == 'EMPLOYEE' || this.loginUserBusinessCategory == 'Provider' && this.paramFieldForce == 'People') {
      this.businessType = '';
      this.personRoles = [];
      this.businessItem = undefined;
    }
    this.isCountryDropdown = false;
    this.isStateDropdown = false;
    this.isVillageDropdown = false;
    this.isCountryChanged = false;
    this.isStateChanged = false;
    this.isVillageChanged = false;
    this.countrySearch = 'India';
    this.address.country = 'India';
    this.stateSearch = 'Enter State';
    this.villageSearch = 'Enter Village';
    this.getDialCode();
    this.getStates();
    this.addPerson.get('level').setValidators(Validators.required);
    this.addPerson.get('level').updateValueAndValidity();
  }

  /**
   * @method openForm
   * @description Opens the add person form and initializes default values.
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    this.personRoleDisabled = false;
    this.countrySearch = 'India';
    this.address.country = 'India';
    this.addPerson.get('countryName')!.setValue('India');
    this.getDialCode();
    this.getStates();
    if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
      this.BAPersonsAvailable.length = localStorage.getItem('alertShown') ? 1 : 0;
      this.businessEntity = JSON.parse(localStorage.getItem('loggedInBusiness'));
      if (this.businessEntity) {
        this.getPersonRolesOnBusinessId(this.businessEntity);
      }
    }
  }

  /**
   * @method removeAddress
   * @description Removes an address from the person's address list.
   * @param {number} i - The index of the address to remove.
   */
  public removeAddress(i: number) {
    this.person.address.splice(i, 1);
  }

  /**
   * @method levelsClose
   * @description Closes the levels view.
   */
  public levelsClose() {
    this.levelClose = false;
    this.levelOpen = true;
  }

  /**
   * @method openLevels
   * @description Opens the levels view.
   */
  public openLevels() {
    this.levelClose = true;
    this.levelOpen = false;
  }

  /**
   * @method customerCloseBlock
   * @description Closes the customer block.
   */
  public customerCloseBlock() {
    this.customerClose = false;
    this.customerOpen = true;
  }

  /**
   * @method openCustomer
   * @description Opens the customer block.
   */
  public openCustomer() {
    this.customerClose = true;
    this.customerOpen = false;
  }
}
